import { getTimeZoneOptions, mapTimeZoneToRepresentative } from "../../utils/timeZoneOptions";
import CalendarEventCard from './CalendarEventCard';
import CalendarDayAgenda from './CalendarDayAgenda';
import CalendarDisplayViews from './CalendarDisplayViews';
import {CALENDAR_DISPLAYS} from './calendarDisplay';
import {agendaDay} from './dayAgenda';
import {calendarActorName,calendarCreatorFields} from './eventCreator';
import CalendarSources from './CalendarSourceControls';
import {calendarSourceDefaults, readCalendarSources, filterCalendarSources, calendarTeamSelected} from './calendarSources';
import {TriangleAlert,Plus, ChevronLeft, ChevronRight, Globe} from 'lucide-react';
import {subscribeCalendarEvents} from "./calendarSubscriptions";
import {occurrenceSetupFields} from "./recurrence";
import EventActionMenu from "./EventActionMenu";
import EventBlockScheduler from "../planning/EventBlockScheduler";
import EventDateControl from "./EventDateControl";
import {saveEventSetup, splitEventSetupSeries, removeEventSetup, ensureEventSetupIds} from "../../utils/eventSetupStore";
import {hasSetupEventId,eventTagLabel} from "../../utils/eventSetup";
import MatchDetailsFields, {calendarMatchDetails} from "./MatchDetailsFields";
import {withSetupTag} from "../planning/eventSetupOptions";
// CalendarView.js
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getFirestore, doc, updateDoc, getDoc, collection, getDocs, onSnapshot } from "firebase/firestore";
import { useTeam } from "../../context/TeamContext";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { eventDateInZone, eventTimeToUTC } from "./eventDates";
import {DateTime} from 'luxon';
import { tagOptions } from "./eventTags";
import { findBookingConflicts } from "./availabilityBookings";

import { expandRecurringAvailabilities } from "./_availabilityShared";
import { getFunctions, httpsCallable } from "firebase/functions";

import CustomAlert from "../core-components/CustomAlert";


import "./CalendarView.css";

export { getTimeZoneOptions, mapTimeZoneToRepresentative } from "../../utils/timeZoneOptions";

// role ordering given by you
const ROLE_ORDER = ["Player", "Coach", "Analyst", "Sub", "Manager", "Spectator", "Trialing"];

const normalizeRole = (r) => {
  const s = String(r || "").toLowerCase().trim();
  if (!s) return "Spectator";
  if (s.startsWith("manag"))   return "Manager";
  if (s.startsWith("coach"))   return "Coach";
  if (s.startsWith("analys"))  return "Analyst";
  if (s.startsWith("player"))  return "Player";
  if (s.startsWith("sub"))     return "Sub";
  if (s.startsWith("spect"))   return "Spectator";
  if (s.startsWith("trial"))   return "Trialing";
  return "Trialing";
};

const ROLE_COLOR_VAR = {
  Manager:   'var(--color-role-manager)',
  Coach:     'var(--color-role-coach)',
  Analyst:   'var(--color-role-analyst)',
  Player:    'var(--color-role-player)',
  Sub:       'var(--color-role-sub)',
  Spectator: 'var(--color-role-spectator)',
  Trialing: 'var(--color-role-trial)',
};

const roleColor = (r) => ROLE_COLOR_VAR[normalizeRole(r)] || 'var(--color-role-spectator)';


const roleRank = (r) => {
  const idx = ROLE_ORDER.findIndex(
    (x) => x.toLowerCase() === normalizeRole(r).toLowerCase()
  );
  return idx === -1 ? ROLE_ORDER.length : idx;
};


const clampPopupPosition = (x, y, width = 280, height = 360) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const clampedX = Math.min(x, viewportWidth - width - 10);
  const clampedY = Math.min(y, viewportHeight - height - 10);

  return { left: clampedX, top: clampedY };
};

const getStartOfWeek = (d) => {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfWeek = (d) => {
  const start = getStartOfWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const isCoveredByAny = (windows, start, end) =>
  (windows || []).some(([s, e]) => s <= start && e >= end);






// CalendarView.js

  const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hour = h.toString().padStart(2, "0");
        const minute = m.toString().padStart(2, "0");
        options.push(`${hour}:${minute}`);
      }
    }
    return options;
  };

  // replace your expandRecurringEvents with this
  const expandRecurringEvents = (events, fromDate, toDate) => {
    const expanded = [];
    const from = new Date(fromDate); from.setHours(0,0,0,0);
    const to = new Date(toDate);

    for (const event of events) {
      const repeat = event.repeat || "none";
      const start  = new Date(event.startUTC);
      const end    = new Date(event.endUTC);
      const durMs  = end - start;

      const exceptions = Array.isArray(event.exceptions) ? event.exceptions : [];
      const overrides  = event.overrides && typeof event.overrides === "object" ? event.overrides : {};

      let until = null;
      if (event.repeatEndDate) {
        const [y,m,d] = event.repeatEndDate.split("-").map(Number);
        until = new Date(y, m-1, d, 23, 59, 59, 999);
      }

      if (repeat === "none") {
        if (end >= from && start <= to) expanded.push(event);
        continue;
      }

      const stepDays =
        repeat === "daily"    ? 1  :
        repeat === "weekly"   ? 7  :
        repeat === "biweekly" ? 14 : 0;

      if (!stepDays) continue; // guard for any unexpected value

      // A moved occurrence may now be outside the original occurrence's week.
      // Render overrides by their saved dates, retaining the original identity.
      for (const [dateKey, override] of Object.entries(overrides)) {
        if (exceptions.includes(dateKey)) continue;
        if (event.repeatEndDate && dateKey > event.repeatEndDate) continue;
        if (new Date(override.endUTC) >= from && new Date(override.startUTC) <= to) {
          expanded.push({ ...event, ...occurrenceSetupFields(event, dateKey), ...override, isRecurringInstance: true, instanceDate: dateKey });
        }
      }

      // find first occurrence on/after `from` aligned to interval
      let nDays = 0;
      if (from > start) {
        const diff = Math.floor((from - start)/86400000);
        const r = diff % stepDays;
        nDays = r === 0 ? diff : diff + (stepDays - r);
      }

      while (true) {
        const instStart = new Date(start);
        instStart.setDate(start.getDate() + nDays);
        if (until && instStart > until) break;
        if (instStart > to) break;

        const dateKey = instStart.toISOString().slice(0,10);
        if (!exceptions.includes(dateKey) && !overrides[dateKey]) {
          const instEnd = new Date(instStart.getTime() + durMs);
          let instance = {
            ...event,
            startUTC: instStart.toISOString(),
            endUTC: instEnd.toISOString(),
            ...occurrenceSetupFields(event, dateKey),
            isRecurringInstance: true,
            instanceDate: dateKey
          };
          if (instEnd >= from) expanded.push(instance);
        }
        nDays += stepDays;
      }
    }
    return expanded;
  };

  
  const tagWithTeamId = (arr, tid) => (arr || []).map(e => ({ ...e, __teamId: tid }));

  const sanitizeEvent = (e) => {
    const out = { ...e };

    out.exceptions    = Array.isArray(out.exceptions) ? out.exceptions : [];
    out.overrides     = out.overrides && typeof out.overrides === "object" ? out.overrides : {};
    out.recurrenceId  = out.recurrenceId != null ? out.recurrenceId : null;
    out.repeatEndDate = out.repeatEndDate != null ? out.repeatEndDate : null;

    if (out.calendar === "team") {
      out.participantsUid = Array.isArray(out.participantsUid) ? out.participantsUid : [];
      out.participants    = Array.isArray(out.participants) ? out.participants : [];

      // ✅ NEW: normalize participantStates map
      out.participantStates =
        out.participantStates && typeof out.participantStates === "object"
          ? out.participantStates
          : {};
    } else {
      delete out.participantsUid;
      delete out.participants;
      delete out.participantStates; // ✅ NEW
    }

    Object.keys(out).forEach((k) => {
      if (out[k] === undefined) delete out[k];
    });

    return out;
  };

  const buildCalendarMutation = ({
    type,
    scope,
    event,
    actorUid = null,
    actorName = "Unknown",
  }) => ({
    type,
    scope,
    actorUid,
    actorName,
    at: new Date().toISOString(),
    eventId: event?.eventId || null,
    recurrenceId: event?.recurrenceId || null,
    instanceDate: event?.instanceDate || null,
    snapshot: sanitizeEvent({
      ...event,
      __teamId: undefined,
      isRecurringInstance: undefined,
    }),
  });

  function updateAfterTeamWrite(setAllEvents, tid, activeTid, updatedTeamEvents) {
    setAllEvents(prev => ({
      ...prev,
      team: tid === activeTid ? updatedTeamEvents : prev.team,
      allTeams: [
        ...(prev.allTeams || []).filter(e => e.__teamId !== tid),
        ...tagWithTeamId(updatedTeamEvents, tid),
      ],
    }));
  }

  // async function applyBusyAcrossOtherTeams({
  //   db,
  //   activeTeamId,
  //   newEvent,
  //   participantRecords // array of { uid, username }
  // }) {
  //   // expand occurrences we want to reflect (next ~60 days)
  //   const occs = expandEventOccurrences(newEvent);

  //   dbg("START applyBusyAcrossOtherTeams", {
  //     activeTeamId,
  //     newEvent: { startUTC: newEvent.startUTC, endUTC: newEvent.endUTC, repeat: newEvent.repeat },
  //     occsCount: occs.length,
  //     participants: participantRecords?.map(x => ({ uid: x.uid, username: x.username })),
  //   });

  //   for (const p of participantRecords) {
  //     if (!p?.uid) continue;

  //     // fetch user's memberships to find "other" teams
  //     const userSnap = await getDoc(doc(db, "users", p.uid));
  //     const userData = userSnap.exists() ? userSnap.data() : {};
  //     const teamIds = gatherUserTeamIds(userData, activeTeamId).filter(
  //       (tid) => tid && tid !== activeTeamId
  //     );
  //     dbg("User other teamIds", { forUid: p.uid, teamIds });

  //     for (const otherTid of teamIds) {
  //       const teamAvailCol = collection(db, "teams", otherTid, "availabilities");

  //       // For each event occurrence, subtract its busy window from that user's availability in the other team
  //       for (const occ of occs) {
  //         const busyStartMs = Date.parse(occ.startUTC);
  //         const busyEndMs = Date.parse(occ.endUTC);
  //         if (!Number.isFinite(busyStartMs) || !Number.isFinite(busyEndMs)) continue;

  //         // "dateKey" = YYYY-MM-DD of the busy occurrence (UTC)
  //         const dateKey = new Date(busyStartMs).toISOString().slice(0, 10);

  //         // Day window (UTC) to expand recurring availabilities for this specific day
  //         const dayStart = new Date(`${dateKey}T00:00:00.000Z`);
  //         const dayEnd = new Date(`${dateKey}T23:59:59.999Z`);

  //         // ✅ Pull fresh docs each occurrence
  //         const qAvail = query(teamAvailCol, where("userId", "==", p.uid));
  //         const snapAvail = await getDocs(qAvail);
  //         const userAvailDocs = snapAvail.docs.map((d) => ({ id: d.id, ...d.data() }));
  //         dbg("Fetched Team avail docs", {
  //           otherTid,
  //           forUid: p.uid,
  //           count: userAvailDocs.length,
  //           docs: userAvailDocs.map(a => ({
  //             id: a.id,
  //             repeat: a.repeat,
  //             startUTC: a.startUTC,
  //             endUTC: a.endUTC,
  //             userId: a.userId,
  //           })),
  //         });

  //         for (const rec of userAvailDocs) {
  //           const repeat = rec.repeat || "none";

  //           if (
  //             rec.autoAdjustedFromEvent &&
  //             rec.adjustedFromTeamId === activeTeamId &&
  //             rec.adjustedFromEventStartUTC === occ.startUTC &&
  //             rec.adjustedFromEventEndUTC === occ.endUTC
  //           ) {
  //             continue;
  //           }

  //           // We will compute the concrete availability window for THIS day as [aStartMs, aEndMs]
  //           let aStartMs = NaN;
  //           let aEndMs = NaN;

  //           if (repeat === "none") {
  //             aStartMs = Date.parse(rec.startUTC);
  //             aEndMs = Date.parse(rec.endUTC);
  //           } else if (repeat === "weekly" || repeat === "biweekly") {
  //             // Expand just this record into this day window:
  //             const inst = expandRecurringAvailabilities([rec], dayStart, dayEnd)[0];
  //             if (!inst) continue;
  //             aStartMs = Date.parse(inst.startUTC);
  //             aEndMs = Date.parse(inst.endUTC);
  //           } else {
  //             continue; // unsupported repeat types
  //           }

  //           if (!Number.isFinite(aStartMs) || !Number.isFinite(aEndMs)) continue;
  //           if (!overlaps(aStartMs, aEndMs, busyStartMs, busyEndMs)) continue;

  //           // subtract busy interval from availability interval -> remainder parts
  //           const parts = subtractInterval(aStartMs, aEndMs, busyStartMs, busyEndMs);
  //           // Safety: if subtraction says "no remainder", verify the busy interval actually fully covers it.
  //           // (Protects against any future conversion bugs.)
  //           if (parts.length === 0) {
  //             const fullyCovered = busyStartMs <= aStartMs && busyEndMs >= aEndMs;
  //             if (!fullyCovered) {
  //               continue; // don't delete the user's availability
  //             }
  //           }

  //           // ✅ Base fields to preserve (critical: scope + createdAt)
  //           const baseFields = {
  //             userId: rec.userId || p.uid,
  //             username: rec.username || p.username || "Member",
  //             repeat: "none",
  //             repeatEndDate: null,
  //             exceptions: [],
  //             overrides: {},
  //             scope: rec.scope || "imported-from-personal",
  //             createdAt: rec.createdAt || Date.now(),
  //             autoAdjustedFromEvent: true,
  //             adjustedAt: new Date().toISOString(),
  //             adjustedFromTeamId: activeTeamId,
  //             adjustedFromEventStartUTC: occ.startUTC,
  //             adjustedFromEventEndUTC: occ.endUTC,
  //           };

  //           const batch = writeBatch(db);

  //           if (repeat === "none") {
  //             // Delete original one-off doc and replace with split segments (atomic)
  //             batch.delete(doc(db, "teams", otherTid, "availabilities", rec.id));

  //             for (const [s, e] of parts) {
  //               if (e <= s) continue;
  //               const newDocRef = doc(teamAvailCol); // auto-id
  //               batch.set(newDocRef, {
  //                 ...baseFields,
  //                 startUTC: new Date(s).toISOString(),
  //                 endUTC: new Date(e).toISOString(),
  //               });
  //             }
  //           } else {
  //             // Recurring availability (weekly/biweekly):
  //             // Your UI/expander does NOT understand overrides[dateKey].segments.
  //             // So we:
  //             // 1) EXCEPTION this date (remove recurring instance for that day)
  //             // 2) CREATE one-off "none" docs for the remaining segments

  //             const recRef = doc(db, "teams", otherTid, "availabilities", rec.id);

  //             const existingExceptions = Array.isArray(rec.exceptions) ? rec.exceptions : [];
  //             const nextExceptions = Array.from(new Set([...existingExceptions, dateKey]));

  //             // 1) remove that day's recurring instance
  //             batch.update(recRef, {
  //               exceptions: nextExceptions,
  //             });

  //             // 2) add remainder as one-off blocks
  //             const remainder = mergeSegments(parts);
  //             for (const [s, e] of remainder) {
  //               if (e <= s) continue;

  //               const newDocRef = doc(teamAvailCol); // auto-id
  //               batch.set(newDocRef, {
  //                 ...baseFields,
  //                 startUTC: new Date(s).toISOString(),
  //                 endUTC: new Date(e).toISOString(),
  //                 repeat: "none",
  //                 repeatEndDate: null,

  //                 // optional traceability
  //                 adjustedFromRecurringAvailId: rec.id,
  //                 adjustedForDateKey: dateKey,
  //               });
  //             }
  //           }

  //           // If busy fully covered the availability window, parts could be []
  //           // Batch still commits (either delete-only or exception-only), which is correct.
  //           try {
  //             await batch.commit();
  //           } catch (err) {
  //             dbg("BATCH COMMIT FAILED", {
  //               code: err?.code,
  //               message: err?.message,
  //               otherTid,
  //               recId: rec.id,
  //             });
  //             // Make it impossible to miss:
  //             alert(`Busy propagation failed: ${err?.code || ""} ${err?.message || err}`);
  //           }
  //         }
  //       }
  //     }
  //   }
  // }

const getNearest15MinuteTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }
    now.setSeconds(0);
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  };

  const CalendarView = ({
    readOnly = false,
    readOnlyMessage = "Your active team can view this calendar, but editing is available once the team is on Basic or higher.",
    editorOnly = false,
    editorValidationPending = false,
    openActions = false,
    actionPosition,
    editEvent = null,
    addDraft = null,
    onEditorClose,
    teamIdOverride = null,
    initialTimeZone = null,
  }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const consumedAvailabilityDraft = useRef(null);
    const db = getFirestore();
    const auth = getAuth();
    const { teamId: activeTeamId, username: ctxUsername, loading: teamLoading } = useTeam();
    const teamId = teamIdOverride || activeTeamId;
    const externalEditStarted = useRef(false), externalEditorWasOpen = useRef(false);
    const [externalMutationPending,setExternalMutationPending] = useState(false);
    const externalEditorRoot = useRef(null);
    const externalClose = useRef(onEditorClose); externalClose.current = onEditorClose;
    const [userId, setUserId] = useState(null);
    const [conflictSet, setConflictSet] = useState(new Set());
    const [showConflictsOnly, setShowConflictsOnly] = useState(false);
    const [authReady, setAuthReady] = useState(false);
    useEffect(() => {
      const unsub = onAuthStateChanged(auth, (u) => {
        setUserId(u?.uid || null);
        setAuthReady(true);
      });
      return unsub;
    }, [auth]);
    const [recurringActionModal, setRecurringActionModal] = useState(null);
    const [editingOverrideInstance, setEditingOverrideInstance] = useState(null);
    const [editingFutureSeries, setEditingFutureSeries] = useState(null);
    const [alertMsg, setAlertMsg] = useState("");
    const [authActionAlert, setAuthActionAlert] = useState("");
    const [isUserLoading, setIsUserLoading] = useState(true);
    const [activeTeamMembers, setTeamMembers] = useState([]); // [{username, uid?}]
    const [activeRosterTeamId, setLoadedRosterTeamId] = useState(null);
    const [teamMaps, setTeamMaps] = useState([]);

    const showLoginRequiredAlert = (
      message = "Please Login to use this function"
    ) => {
      setAuthActionAlert(message);
    };

    const showReadOnlyAlert = (
      message = readOnlyMessage
    ) => {
      setAlertMsg(message);
    };

    // =====================
    // Participation Requests
    // =====================
    const functions = getFunctions();
    const requestEventParticipationFn = useMemo(
      () => httpsCallable(functions, "requestEventParticipation"),
      [functions]
    );

    // cleanup callable for deleting all participationRequests for an event
    const deleteParticipationRequestsForEventFn = useMemo(
      () => httpsCallable(functions, "deleteParticipationRequestsForEvent"),
      [functions]
    );

    // We need a stable eventId even before saving, so requests can attach to "this event"
    const [draftEventId, setDraftEventId] = useState(null);

    // Local immediate UI feedback (even before Firestore roundtrip refresh)
    const [localRequestStatusByUid, setLocalRequestStatusByUid] = useState({});

    // Queue requests while popup is open; only send after event is successfully saved
    // shape: { [uid]: { targetUid, targetUsername, queuedAt } }
    const [queuedParticipationRequests, setQueuedParticipationRequests] = useState({});

    // shape: { [uid]: { status: "pending"|"accepted"|"declined", at: number } }

// ✅ Keep badges in sync without requiring a reload
const applyParticipantStatesToEventInState = (eventId, updatesByUid) => {
  if (!eventId || !updatesByUid) return;

  const apply = (arr) =>
    (arr || []).map((evt) => {
      if (!evt || evt.eventId !== eventId) return evt;
      return {
        ...evt,
        participantStates: {
          ...(evt.participantStates || {}),
          ...updatesByUid,
        },
      };
    });

  setAllEvents((prev) => ({
    ...prev,
    team: apply(prev.team),
    allTeams: apply(prev.allTeams),
  }));
};


    // Team availability (raw + fast lookup index for the selected week)
    const [teamAvailRaw, setTeamAvailRaw] = useState([]);
    const [teamAvailIndex, setTeamAvailIndex] = useState({ byUid: {}, byName: {}, ready: false });

    const isPointerFine = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(pointer: fine)").matches
      : true;
    
    // ✅ MOBILE detection (touch / small screens)
    const [isMobileUI, setIsMobileUI] = useState(() => {
      if (typeof window === "undefined" || !window.matchMedia) return false;
      return window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(max-width: 46rem)").matches;
    });

    useEffect(() => {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq1 = window.matchMedia("(pointer: coarse)");
      const mq2 = window.matchMedia("(max-width: 46rem)");
      const handler = () => setIsMobileUI(mq1.matches || mq2.matches);

      // Safari fallback: addListener/removeListener if needed
      if (mq1.addEventListener) {
        mq1.addEventListener("change", handler);
        mq2.addEventListener("change", handler);
      } else {
        mq1.addListener(handler);
        mq2.addListener(handler);
      }
      handler();

      return () => {
        if (mq1.removeEventListener) {
          mq1.removeEventListener("change", handler);
          mq2.removeEventListener("change", handler);
        } else {
          mq1.removeListener(handler);
          mq2.removeListener(handler);
        }
      };
    }, []);

    // ✅ Open "Add event" popup in a mobile-safe position (bottom sheet)
    const openAddPopupSmart = (date, clientX, clientY) => {
      if (!authReady) return;

      if (!userId) {
        showLoginRequiredAlert("Please Login to use this function");
        return;
      }

      if (readOnly) {
        showReadOnlyAlert();
        return;
      }

      if (isMobileUI) {
        openAddPopup(
          date,
          Math.round(window.innerWidth / 2),
          Math.round(window.innerHeight * 0.8)
        );
      } else {
        openAddPopup(date, clientX, clientY);
      }
    };

    // ✅ Open "Edit/Delete" menu in a mobile-safe position (bottom sheet)
    const openContextMenuSmart = (eventObj, clientX, clientY) => {
      if (isMobileUI) {
        setContextEvent({
          event: eventObj,
          x: Math.round(window.innerWidth / 2),
          y: Math.round(window.innerHeight * 0.8),
          isRecurring: eventObj.repeat && eventObj.repeat !== "none",
          isRecurringInstance: eventObj.isRecurringInstance
        });
      } else {
        setContextEvent({
          event: eventObj,
          x: clientX,
          y: clientY,
          isRecurring: eventObj.repeat && eventObj.repeat !== "none",
          isRecurringInstance: eventObj.isRecurringInstance
        });
      }
    };

    // ✅ Long-press helper (for mobile "right click" equivalent)
    const useLongPress = (onLongPress, ms = 450) => {
      const tRef = useRef(null);
      const movedRef = useRef(false);

      const clear = () => {
        if (tRef.current) clearTimeout(tRef.current);
        tRef.current = null;
        movedRef.current = false;
      };

      const start = (e) => {
        clear();
        movedRef.current = false;
        tRef.current = setTimeout(() => {
          if (!movedRef.current) onLongPress(e);
        }, ms);
      };

      const move = () => { movedRef.current = true; clear(); };
      const end = () => clear();

      return { start, move, end };
    };

    const monthCellLongPress = useLongPress((e) => {
      // only if the user pressed a real day cell
      // we store the date in a data attribute in the JSX below
      const iso = e.currentTarget?.dataset?.dateiso;
      if (!iso) return;
      const d = new Date(iso);
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      openAddPopupSmart(d, cx, cy);
    });

    const eventLongPress = useLongPress((e) => {
      const payload = e.currentTarget?.__evt;
      if (!payload) return;
      openContextMenuSmart(payload, 120, 120);
    });

    const openAddPopup = useCallback((date, clientX, clientY) => {
      const { left, top } = clampPopupPosition(clientX, clientY);
      
      setDraftEventId(uuidv4());
      setLocalRequestStatusByUid({});
      
      const start = getNearest15MinuteTime();
      const [sh, sm] = start.split(":").map(Number);
      const endH = Math.min(sh + 1, 23);
      const endM = sh === 23 ? Math.min(sm, 45) : sm;
      const end = `${String(endH).padStart(2,"0")}:${String(endM).padStart(2,"0")}`;

      setEventPopup({
        x: left, 
        y: top,
        date,
        isEditing: false
      });
      setFormData({
        eventDate: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
        startTime: start,
        endTime: end,            // ⬅️ default 1 hour later
        repeat: "none",
        repeatEndDate: "",
        tag: "",
        calendar: "personal",
        teamId,
        reminder: "",
        customNote: "",
        participants: [],
        mapId: "",
        mapName: "",
      });
      setHasEditedEndTime(false);
      setMissingFields([]);
      setErrorMessage("");
    },[teamId]);

  
    const today = new Date();
    const [viewDate, setViewDate] = useState(today);
    const [selectedDate, setSelectedDate] = useState(today);
    const [dayAgendaExpanded,setDayAgendaExpanded] = useState(()=>{
      try {const stored=sessionStorage.getItem('calendar.dayAgenda');return stored?stored==='expanded':window.innerWidth>=1600;}catch{return window.innerWidth>=1600;}
    });
    const changeDayAgendaExpanded=useCallback(open=>{setDayAgendaExpanded(open);try{sessionStorage.setItem('calendar.dayAgenda',open?'expanded':'collapsed');}catch{}},[]);
    const selectAgendaDate=useCallback(date=>{setSelectedDate(date);setViewDate(date);},[]);
    // —————————————————————————————————————————————
    // only show occurrences within the month currently in view
    const monthStart = useMemo(
      () => new Date(viewDate.getFullYear(), viewDate.getMonth(), 1),
      [viewDate]
    );
    const monthEnd = useMemo(
      () => {
        const d = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
        d.setHours(23, 59, 59, 999);
        return d;
      },
      [viewDate]
    );
    // —————————————————————————————————————————————
    const [eventPopup, setEventPopup] = useState(null);
    const popupRef = useRef();
    const TZ_SESSION_KEY = "calendar.sessionTimeZone";

    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [selectedTimeZone, setSelectedTimeZone] = useState(() => {
      const sessionTz = sessionStorage.getItem(TZ_SESSION_KEY);
      const base = initialTimeZone || sessionTz || systemTimeZone;
      return mapTimeZoneToRepresentative(base);
    });

    // keep sessionStorage in sync so other calendar routes use the same selection this session
    useEffect(() => {
      if (selectedTimeZone) {
        sessionStorage.setItem(TZ_SESSION_KEY, selectedTimeZone);
      }
    }, [selectedTimeZone]);

    // Persist the user's last active timezone for notifications
    useEffect(() => {
      // only write if we know who the user is
      if (!authReady || !userId || !selectedTimeZone) return;

      // Write to users/{uid}
      const write = async () => {
        try {
          await updateDoc(doc(db, "users", userId), {
            lastActiveTimeZone: selectedTimeZone,
            lastActiveTimeZoneUpdatedAt: new Date().toISOString(),
          });
        } catch (e) {
          // Non-fatal: silently ignore; calendar continues to work
          console.warn("Failed to save lastActiveTimeZone:", e);
        }
      };
      write();
    }, [authReady, userId, selectedTimeZone, db]);

    // add "allTeams" and modernize the view modes
    const [allEvents, setAllEvents] = useState({ personal: [], team: [], allTeams: [] });
    const [teamNameById, setTeamNameById] = useState({});
    const [agendaTeams,setAgendaTeams] = useState({});
    const getSourceLabel = (evt) => {
      if (!evt) return "";
      if (evt.calendar === "team") {
        const tid = evt.__teamId || teamId;
        return teamNameById[tid] || "Team";
      }
      return "Personal";
    };


    const [sourcePreference, setSourcePreference] = useState({owner: null, value: null});
    const sourceSelection = useMemo(() => sourcePreference.owner === userId && sourcePreference.value
      ? sourcePreference.value : calendarSourceDefaults('all', teamId), [sourcePreference, userId, teamId]);
    const selectedSourceNames = useMemo(() => [
      ...(sourceSelection.personal ? ['Personal Calendar'] : []),
      ...Object.entries(teamNameById).filter(([id]) => calendarTeamSelected(sourceSelection,id)).map(([,name]) => name),
    ], [sourceSelection, teamNameById]);
    useEffect(() => {
      if (!userId) {setSourcePreference({owner: null, value: null}); return;}
      const value = readCalendarSources(sessionStorage, userId, teamId);
      setSourcePreference({owner: userId, value});
      try {sessionStorage.setItem('calendar.sources.v1.' + userId, JSON.stringify(value));} catch {}
    }, [userId, teamId]);
    const toggleCalendarSource = (id, checked) => {
      const value = id === 'personal' ? {...sourceSelection, personal: checked}
        : {...sourceSelection, teams: {...sourceSelection.teams, [id]: checked}};
      setSourcePreference({owner: userId, value});
      try {sessionStorage.setItem('calendar.sources.v1.' + userId, JSON.stringify(value));} catch {}
    };
    const selectAllCalendars = () => {
      const value = calendarSourceDefaults('all', teamId);
      setSourcePreference({owner: userId, value});
      try {sessionStorage.setItem('calendar.sources.v1.' + userId, JSON.stringify(value));} catch {}
    };

    const [calendarView, setCalendarView] = useState(() => {
      return sessionStorage.getItem("calendarMode") || "week"; // "day" | "week"
    });

    const [displayPreference,setDisplayPreference]=useState(null);
    const display=displayPreference?.uid===userId?displayPreference.value:'detailed';
    useEffect(()=>{
      if(!userId)return;
      let value='detailed';
      try {const saved=localStorage.getItem('calendar.display.v1.'+userId);if(CALENDAR_DISPLAYS.some(([id])=>id===saved))value=saved;}catch{}
      setDisplayPreference({uid:userId,value});
    },[userId]);
    const changeDisplay=value=>{
      setDisplayPreference({uid:userId,value});
      if(userId)try{localStorage.setItem('calendar.display.v1.'+userId,value);}catch{}
    };

    const setViewAndPersist = (mode) => {
      setCalendarView(mode);
      sessionStorage.setItem("calendarMode", mode);
    };

    const [formData, setFormData] = useState({
      startTime: "",
      endTime: "",
      repeat: "none",
      repeatEndDate: "", 
      tag: "",
      calendar: "personal",
      reminder: "",
      customNote: "",
      participants: [],  
      mapId: "",
      mapName: "",
    });    

    const editorTeamId = eventPopup ? (formData.teamId || teamId) : teamId;
    const [otherTeamRoster, setOtherTeamRoster] = useState({teamId: null, members: []});
    const teamMembers = useMemo(() => editorTeamId === teamId ? activeTeamMembers : (otherTeamRoster.teamId === editorTeamId ? otherTeamRoster.members : []), [editorTeamId, teamId, activeTeamMembers, otherTeamRoster]);
    const loadedRosterTeamId = editorTeamId === teamId ? activeRosterTeamId : otherTeamRoster.teamId;
    const editorOpen = Boolean(eventPopup);
    useEffect(() => {
      if (!editorOpen || !editorTeamId || editorTeamId === teamId) return undefined;
      let active = true, version = 0;
      setOtherTeamRoster({teamId: null, members: []});
      const stop = onSnapshot(doc(db, 'teams', editorTeamId), snapshot => {
        const request = ++version;
        if (!snapshot.exists()) return;
        void deriveTeamMembersAsync(snapshot.data(), db).then(members => {
          if (active && request === version) setOtherTeamRoster({teamId: editorTeamId, members});
        }).catch(() => {if (active) setOtherTeamRoster({teamId: null, members: []});});
      }, () => {if (active) setOtherTeamRoster({teamId: null, members: []});});
      return () => {active = false; stop();};
    }, [db, editorTeamId, teamId, editorOpen]);

    useEffect(() => {
      if (!editorTeamId) {
        setTeamAvailIndex({ byUid: {}, byName: {}, ready: false });
        return;
      }
      // compute week window for the popup if open; otherwise selected date
      const referenceDate = eventPopup && formData.eventDate
        ? new Date(`${formData.eventDate}T12:00:00`)
        : eventPopup?.date || selectedDate;
      const ws = getStartOfWeek(referenceDate);
      const we = getEndOfWeek(referenceDate);

      const expanded = expandRecurringAvailabilities(teamAvailRaw || [], ws, we);

      const byUid = {};
      const byName = {};
      expanded.forEach((rec) => {
        const s = Date.parse(rec.startUTC);
        const e = Date.parse(rec.endUTC);

        // ✅ UID normalization (handles older/newer field names)
        const uid =
          rec.userId ||
          rec.uid ||
          rec.userUid ||
          rec.createdByUid ||
          null;

        if (uid) {
          (byUid[uid] ||= []).push([s, e]);
        }

        // ✅ Username normalization (case-insensitive matching)
        const unameRaw = rec.username || rec.userName || rec.name || "";
        const uname = String(unameRaw).trim();
        if (uname) {
          (byName[uname.toLowerCase()] ||= []).push([s, e]);
        }
      });

      setTeamAvailIndex({ byUid, byName, ready: true });
    }, [editorTeamId, teamAvailRaw, selectedDate, eventPopup, formData.eventDate]);



  const filteredEvents = useMemo(() => filterCalendarSources(allEvents, sourceSelection, teamId), [allEvents, sourceSelection, teamId]);

  // ✅ Memoize expanded events based on filtered list
  const expandedFilteredEvents = useMemo(() => {
    return expandRecurringEvents(filteredEvents, monthStart, monthEnd);
  }, [filteredEvents, monthStart, monthEnd]);
  const expandedAgendaEvents=useMemo(()=>{
    const day=agendaDay(selectedDate,selectedTimeZone);
    const week=day.minus({days:day.weekday-1});
    // Include the whole viewing-zone week for the agenda's day indicators, plus
    // overnight occurrences at its edges, independently of the displayed month.
    return expandRecurringEvents(filteredEvents,week.minus({days:1}).toJSDate(),week.plus({days:8}).toJSDate());
  },[filteredEvents,selectedDate,selectedTimeZone]);


    const [contextEvent, setContextEvent] = useState(null);
    const dayTimelineRef = useRef();
    const eventRefs = useRef([]);
    const scheduleScrollRef = useRef(null);
    useEffect(()=>{
      // Timeline views start at the first scheduled hour. Other displays begin
      // at their heading instead of inheriting that timeline scroll position.
      if(display!=='detailed'&&scheduleScrollRef.current)scheduleScrollRef.current.scrollTop=0;
    },[display]);

    useEffect(() => {
      if (calendarView !== "day") return;
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (eventRefs.current.length > 0 && dayTimelineRef.current) {
            const firstEvent = eventRefs.current[0];
            dayTimelineRef.current.scrollTo({
              top: Math.max(firstEvent.offsetTop - 20, 0),
              behavior: "smooth",
            });
          }
        }, 0);
      });
    }, [calendarView, selectedDate, expandedFilteredEvents, selectedTimeZone]);

    
  
  
    const [hasEditedEndTime, setHasEditedEndTime] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [bookingConflict, setBookingConflict] = useState(null);
    const [acceptedBookingConflict, setAcceptedBookingConflict] = useState(null);

    // Week window based on the currently *selected* date
    const weekStart = useMemo(() => getStartOfWeek(selectedDate), [selectedDate]);
    const weekEnd   = useMemo(() => getEndOfWeek(selectedDate), [selectedDate]);

    // Expand just what we need for the week view
    const expandedWeekEvents = useMemo(() => {
      return expandRecurringEvents(filteredEvents, weekStart, weekEnd);
    }, [filteredEvents, weekStart, weekEnd]);

  
  const scheduleStartHour = useMemo(() => {
    const starts = expandedWeekEvents.map(event => new Date(new Date(event.startUTC).toLocaleString('en-US', {timeZone: selectedTimeZone})))
      .filter(date => calendarView !== 'day' || date.toDateString() === selectedDate.toDateString());
    return starts.length ? Math.max(0, Math.min(...starts.map(date => date.getHours())) - 1) : 8;
  }, [expandedWeekEvents, selectedTimeZone, calendarView, selectedDate]);
  useEffect(() => {
    const scroll = scheduleScrollRef.current;
    const hour = scroll?.querySelectorAll('.timeline-hour-label')[scheduleStartHour];
    if (!scroll || !hour) return;
    const headingHeight = (scroll.querySelector('.day-view-header-row')?.getBoundingClientRect().height || 0)
      + (scroll.querySelector('.week-strip-header')?.getBoundingClientRect().height || 0);
    scroll.scrollTop += hour.getBoundingClientRect().top - scroll.getBoundingClientRect().top - headingHeight;
  }, [calendarView, selectedDate, scheduleStartHour, display]);

  async function deriveTeamMembersAsync(teamData = {}, db) {
    const raw = [];
    const teamRolesMap = teamData.teamRoles || {};

    // ⬇️ You accidentally deleted this whole section — add it back:
    if (Array.isArray(teamData.members)) {
      teamData.members.forEach(m => {
        if (typeof m === "string") raw.push({ raw: m }); // usually a uid
        else if (m && typeof m === "object") {
          raw.push({
            uid: m.uid,
            username: m.username,
            email: m.email,
            role: m.role || m.position || m.title || m.teamRole || (Array.isArray(m.roles) ? m.roles[0] : undefined),
          });
        }
      });
    }
    if (Array.isArray(teamData.memberUsernames)) {
      teamData.memberUsernames.forEach(u => raw.push({ username: String(u) }));
    }
    if (teamData.roster && typeof teamData.roster === "object") {
      Object.values(teamData.roster).forEach(m => {
        if (typeof m === "string") raw.push({ raw: m });
        else if (m && typeof m === "object") {
          raw.push({
            uid: m.uid,
            username: m.username,
            email: m.email,
            role: m.role || m.position || m.title || m.teamRole || (Array.isArray(m.roles) ? m.roles[0] : undefined),
          });
        }
      });
    }

    const results = [];
    const uidLookups = [];
    const looksLikeUid = (s) =>
      typeof s === "string" && !s.includes("@") && s.length >= 20 && s.length <= 40;

    for (const entry of raw) {
      if (entry.username && entry.username.trim()) {
        results.push({
          username: entry.username.trim(),
          uid: entry.uid || null,
          role: normalizeRole(entry.uid ? teamRolesMap[entry.uid] : entry.role),
        });
        continue;
      }
      const rawVal = entry.raw || entry.email || "";
      if (looksLikeUid(rawVal) || entry.uid) {
        const uid = entry.uid || rawVal;
        results.push({
          username: uid, // temp
          uid,
          role: normalizeRole(teamRolesMap[uid]),
        });
        uidLookups.push({ uid, index: results.length - 1 });
        continue;
      }
      if (typeof rawVal === "string" && rawVal.includes("@")) {
        results.push({ username: rawVal.split("@")[0], uid: entry.uid || null, role: "Spectator" });
        continue;
      }
      if (typeof rawVal === "string" && rawVal.trim()) {
        results.push({ username: rawVal.trim(), uid: entry.uid || null, role: "Spectator" });
        continue;
      }
    }

    if (uidLookups.length) {
      await Promise.all(uidLookups.map(async ({ uid, index }) => {
        try {
          const snap = await getDoc(doc(db, "uids", uid));
          if (snap.exists()) {
            const u = snap.data();
            const name = u.username;
            const roleFromTeam = teamRolesMap[uid] ? normalizeRole(teamRolesMap[uid]) : null;

            results[index] = {
              username: name || results[index].username,
              uid,
              role: roleFromTeam || results[index].role || "Spectator",
            };
          }
        } catch (e) {
          console.warn("Username lookup failed for uid:", uid, e);
        }
      }));
    }

    const seen = new Set();
    const deduped = results.filter(m => {
      const key = (m.username || "").toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    deduped.sort((a, b) => {
      const rr = roleRank(a.role) - roleRank(b.role);
      if (rr !== 0) return rr;
      return String(a.username).localeCompare(String(b.username), undefined, { sensitivity: "base" });
    });

    return deduped;
  }

  // --- Cross-team "busy" propagation helpers -------------------------------

  // expand event to concrete occurrences (next 60 days max)
  
  
  // does a weekly/biweekly availability "occur" on this date?
  



  
    useEffect(() => {
      setAllEvents({personal: [], team: [], allTeams: []});
      setTeamMembers([]); setTeamAvailRaw([]); setTeamNameById({}); setAgendaTeams({});
      setLoadedRosterTeamId(null);
      if (!authReady || !userId) {setIsUserLoading(!authReady); return undefined;}
      let active = true, memberVersion = 0, memberSignature = null;
      const repairedScopes = new Set();
      setIsUserLoading(true);
      const stopEvents = subscribeCalendarEvents({db, userId, teamId,
        onChange: ({events, names, activeTeam, teams}) => {
          if (!active) return;
          setAllEvents(events); setTeamNameById(names); setAgendaTeams(teams || {}); setIsUserLoading(false);
          if (!readOnly) for (const [scope, rows] of [["team", events.team], ["personal", events.personal]]) {
            if (!repairedScopes.has(scope) && (scope !== "team" || teamId) && rows.some(event => event && !hasSetupEventId(event))) {
              repairedScopes.add(scope);
              void ensureEventSetupIds({teamId, scope}).catch(error => console.warn("Calendar identity repair failed:", error));
            }
          }
          const signature = JSON.stringify([activeTeam?.members, activeTeam?.memberUsernames, activeTeam?.teamRoles, activeTeam?.roster]);
          if (signature === memberSignature) return;
          memberSignature = signature;
          const version = ++memberVersion;
          if (!activeTeam) {setTeamMembers([]); setLoadedRosterTeamId(null); return;}
          setLoadedRosterTeamId(null);
          void deriveTeamMembersAsync(activeTeam, db).then(members => {
            if (active && version === memberVersion) {setTeamMembers(members); setLoadedRosterTeamId(teamId);}
          }).catch(error => {if (active && version === memberVersion) setTeamMembers([]); console.warn("Calendar member lookup failed:", error);});
        },
        onError: error => {if (active) {setIsUserLoading(false); console.warn("Calendar subscription failed:", error);}},
      });
      return () => {active = false; stopEvents();};
    }, [authReady, userId, teamId, db, readOnly]);

    useEffect(() => {
      setTeamAvailRaw([]);
      if (!authReady || !userId || !editorTeamId) return undefined;
      return onSnapshot(collection(db, "teams", editorTeamId, "availabilities"), snapshot => {
        setTeamAvailRaw(snapshot.docs.map(item => ({id: item.id, ...item.data()})));
      }, () => setTeamAvailRaw([]));
    }, [db, authReady, userId, editorTeamId]);

    useEffect(() => {
      let cancelled = false;

      async function loadTeamMaps() {
        if (!editorTeamId) {
          if (!cancelled) setTeamMaps([]);
          return;
        }

        try {
          const snapshot = await getDocs(collection(db, "teams", editorTeamId, "maps"));
          if (!cancelled) {
            setTeamMaps(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
          }
        } catch (error) {
          console.warn("Unable to load calendar map options:", error);
          if (!cancelled) setTeamMaps([]);
        }
      }

      void loadTeamMaps();
      return () => { cancelled = true; };
    }, [db, editorTeamId]);

    

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setEventPopup(null);
      }
    };
    if (eventPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [eventPopup]);

  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();

  const getMonthDays = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = (firstDay + 6) % 7;
    const cellCount = Math.ceil((offset + daysInMonth) / 7) * 7;
    // Date normalizes offsets outside this month, including year boundaries.
    return Array.from({ length: cellCount }, (_, i) => i + 1 - offset);
  };

  const days = getMonthDays(currentYear, currentMonth);

  const handleRightClick = (e, day) => {
    e.preventDefault();
    if (day === null) return;

    const clicked = new Date(currentYear, currentMonth, day);
    openAddPopupSmart(clicked, e.clientX, e.clientY);
  };

  const closePopup = () => {
    setEventPopup(null);
    setMissingFields([]);
    setErrorMessage("");

    // ✅ critical: cancel means NO requests sent
    setQueuedParticipationRequests({});
    setLocalRequestStatusByUid({});
    setDraftEventId(null);
  };
  
  const moveSchedule = direction => {
    if(display==='month'){
      const next=new Date(viewDate.getFullYear(),viewDate.getMonth()+direction,1);
      setViewDate(next);setSelectedDate(next);return;
    }
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + direction * (display==='list'||calendarView === 'week' ? 7 : 1));
    setSelectedDate(next); setViewDate(next);
  };
  const goToPreviousMonth = () => setViewDate(new Date(currentYear, currentMonth - 1, 1));
  const goToNextMonth = () => setViewDate(new Date(currentYear, currentMonth + 1, 1));
  const goToToday = () => {
    setViewDate(today);
    setSelectedDate(today);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "tag") {
      try {setFormData(withSetupTag(formData, value)); setErrorMessage("");}
      catch (error) {setErrorMessage(error.message); setAlertMsg(error.message);}
      return;
    }
  
    if (name === "endTime") {
      setHasEditedEndTime(true);
    }
  
    setFormData((prev) => {
      let newEndTime = prev.endTime;
  
      if (name === "startTime" && !hasEditedEndTime) {
        const [startHour, startMinute] = value.split(":").map(Number);
        let endHour = startHour + 1;
        let endMinute = startMinute;
  
        if (endHour >= 24) {
          endHour = 23;
          endMinute = 45;
        }
  
        newEndTime = `${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`;
      }
  
      return {
        ...prev,
        [name]: value,
        ...(name === "startTime" && !hasEditedEndTime ? { endTime: newEndTime } : {}),
      };
    });
  };

  // Build a UTC ISO from the popup's date + "HH:MM" time in the selected TZ
  const toUtcISOStringAt = useCallback((date, time) =>
    eventTimeToUTC(date, time, selectedTimeZone), [selectedTimeZone]);



  // Get [startMs, endMs] for the event that's being created/edited (returns null if not ready)
  const getCurrentEventWindowUtc = useCallback(() => {
    if (!eventPopup) return null;
    if (!formData.startTime || !formData.endTime) return null; // ⬅️ new guard
    const sISO = toUtcISOStringAt(formData.eventDate ?? eventPopup.date, formData.startTime);
    const eISO = toUtcISOStringAt(formData.eventDate ?? eventPopup.date, formData.endTime);
    if (!sISO || !eISO) return null;
    return [Date.parse(sISO), Date.parse(eISO)];
  }, [eventPopup, formData.eventDate, formData.endTime, formData.startTime, toUtcISOStringAt]);

  // Check one member by username/uid against the current event window
  const isMemberAvailableForCurrentWindow = useCallback((username, uid) => {
    const win = getCurrentEventWindowUtc();
    if (!win) return true; // can't evaluate yet → don't warn
    const [startMs, endMs] = win;
    const windows =
      (uid && teamAvailIndex.byUid[uid]) ||
      teamAvailIndex.byName[String(username || "").toLowerCase()] ||
      [];
    if (!windows.length) return false; // no availability this week
    return isCoveredByAny(windows, startMs, endMs);
  }, [getCurrentEventWindowUtc, teamAvailIndex.byName, teamAvailIndex.byUid]);

  // Show a non-blocking warning if that member likely isn't available
  const warnIfUnavailable = (member) => {
    if (formData.calendar !== "team") return;
    if (!teamAvailIndex.ready) return;
    if (!eventPopup || !formData.startTime || !formData.endTime) return;
    const ok = isMemberAvailableForCurrentWindow(member.username, member.uid);
    if (!ok) {
      setAlertMsg(`'${member.username}' may have an availability conflict. A confirmation request will be sent automatically after this event is saved.`);
    }
  };

  const getEffectiveRequestStatus = (memberUid) => {
    // Prefer Firestore-sourced status if it exists on the event
    const evt = eventPopup?.originalEvent;
    const fromEvent =
      evt?.participantStates &&
      memberUid &&
      typeof evt.participantStates === "object" &&
      evt.participantStates[memberUid];

    if (fromEvent?.status) return String(fromEvent.status);

    // Otherwise use local optimistic status
    const local = localRequestStatusByUid?.[memberUid];
    if (local?.status) return local.status;

    return null;
  };

  const queueParticipationRequest = (member) => {
    if (member?.uid && member.uid === userId) {
      setAlertMsg("You can’t request participation confirmation from yourself.");
      return;
    }

    if (!member?.uid) {
      setAlertMsg("This team member is missing a UID (cannot queue request).");
      return;
    }
    if (!teamId) {
      setAlertMsg("No active team found.");
      return;
    }
    if (!eventPopup?.date) {
      setAlertMsg("No event date selected.");
      return;
    }

    // ✅ IMPORTANT CHANGE:
    // Do NOT require tag/start/end here.
    // The request is only sent AFTER the event is saved, so those fields will exist then.

    setQueuedParticipationRequests((prev) => ({
      ...prev,
      [member.uid]: {
        targetUid: member.uid,
        targetUsername: member.username,
        queuedAt: Date.now(),
      },
    }));

    setLocalRequestStatusByUid((prev) => ({
      ...prev,
      [member.uid]: { status: "queued", at: Date.now() },
    }));

    // Helpful UX message (even if tag/time not picked yet)
    const missing = [];
    if (!formData?.startTime || !formData?.endTime) missing.push("time");
    if (!formData?.tag) missing.push("tag");

    setAlertMsg(
      missing.length
        ? `Queued request for '${member.username}'. Pick ${missing.join(" + ")} and click “Add” to send.`
        : `Queued request for '${member.username}' (will send when you click “Add”).`
    );
  };



  const flushQueuedParticipationRequests = async ({ savedEventObj }) => {
    // only for team events
    if (!savedEventObj || savedEventObj.calendar !== "team") return;

    const queued = Object.values(queuedParticipationRequests || {});
    if (!queued.length) return;

    try {
      const payloadBase = {
        teamId: editorTeamId,
        eventId: savedEventObj.eventId,
        instanceDate: savedEventObj.instanceDate || eventPopup?.originalEvent?.instanceDate || null,
        eventTag: savedEventObj.tag,
        eventStartUTC: savedEventObj.startUTC,
        eventEndUTC: savedEventObj.endUTC,
        timeZone: selectedTimeZone,
        note: savedEventObj.note || "",
        participantUsernames: (savedEventObj.participants || []).filter(Boolean),
      };

      // mark queued -> pending optimistically
      setLocalRequestStatusByUid((prev) => {
        const next = { ...prev };
        queued.forEach((q) => {
          next[q.targetUid] = { status: "pending", at: Date.now() };
        });
        return next;
      });


// ✅ also reflect pending status directly on the saved event in local state
const pendingStatePatch = {};
queued.forEach((q) => {
  if (!q?.targetUid) return;
  pendingStatePatch[q.targetUid] = { status: "pending", at: Date.now() };
});
applyParticipantStatesToEventInState(savedEventObj.eventId, pendingStatePatch);

      await Promise.all(
        queued.map((q) =>
          requestEventParticipationFn({
            ...payloadBase,
            targetUid: q.targetUid,
          })
        )
      );

      setAlertMsg("Participation requests sent.");
      setQueuedParticipationRequests({});
// ✅ pull the latest team events so any server-side updates show immediately
try {
  const teamSnap = await getDoc(doc(db, "teams", editorTeamId));
  if (teamSnap.exists()) {
    const teamData = teamSnap.data() || {};
    const teamEvents = Array.isArray(teamData.events) ? teamData.events : [];
    updateAfterTeamWrite(setAllEvents, teamId, editorTeamId, teamEvents);
  }
} catch (e) {
  // non-fatal: UI already updated optimistically
}

    } catch (e) {
      console.error("Failed to flush queued participation requests:", e);
      setAlertMsg("Event saved, but failed to send participation requests.");
      // keep queue so user could try again (or you can clear it if you prefer)
    }
  };

  const handleSaveEvent = async () => {
    if (editorValidationPending) {setErrorMessage('Checking current event access. Please try saving again in a moment.');return;}
    const { startTime, endTime, tag, calendar, reminder, mapId, mapName } = formData;
    const { date, isEditing, originalEvent } = eventPopup;

    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    if (!userId) {
      showLoginRequiredAlert("Please Login to use this function");
      return;
    }

    if (calendar === "team" && !editorTeamId) {
      setErrorMessage("Join a team to add team events.");
        return;
    }
  
    if (calendar === "team" && (loadedRosterTeamId !== editorTeamId || !teamNameById[editorTeamId])) {setErrorMessage("Wait for the selected team's roster to finish loading, then try again."); return;}
    const missing = [];
    if (!tag) missing.push("tag");
    if (!startTime) missing.push("startTime");
    if (!endTime) missing.push("endTime");
  
    if (missing.length > 0) {
      setMissingFields(missing);
      setErrorMessage("Missing Information");
      return;
    }
    const startUTC = toUtcISOStringAt(formData.eventDate ?? date, startTime);
    const endUTC = toUtcISOStringAt(formData.eventDate ?? date, endTime);
    if (!startUTC || !endUTC || endUTC <= startUTC) {
      setErrorMessage("Please choose a valid date and an end time after the start time. Some times do not exist during daylight-saving changes.");
      return;
    }
    // Optional, non-blocking conflicts heads-up
    if (calendar === "team" && teamAvailIndex.ready && (formData.participants || []).length) {
      const conflicts = (formData.participants || []).reduce((arr, name) => {
        const nameKey = String(name || "").toLowerCase();
        const m = teamMembers.find(x => String(x.username || "").toLowerCase() === nameKey);
        if (m && !isMemberAvailableForCurrentWindow(m.username, m.uid)) arr.push(name);
        return arr;
      }, []);
      if (conflicts.length) {
        const list =
          conflicts.slice(0, 3).map(n => `'${n}'`).join(", ") +
          (conflicts.length > 3 ? ", …" : "");
        setAlertMsg(`Double check with ${list}, as event might conflict with their availabilities`);
      }
    }

  
    const toUtcISOString = (timeStr) => {
      const iso = toUtcISOStringAt(formData.eventDate ?? date, timeStr);
      if (!iso) throw new Error("Failed to convert time to UTC");
      return iso;
    };

    // Resolve selected participant records
    const selectedNamesLower = new Set((formData.participants || []).map(n => String(n).toLowerCase()));
    const selectedMembers = (teamMembers || []).filter(m =>
      selectedNamesLower.has(String(m.username || "").toLowerCase())
    );

    // Collect UIDs for stable targeting
    const participantsUid = selectedMembers
      .map(m => m.uid)
      .filter(Boolean); // drop nulls

    if (
      calendar === "team" &&
      (formData.participants || []).length > 0 &&
      participantsUid.length === 0
    ) {
      setAlertMsg(
        "The selected participants could not be matched to team member UIDs, so add-to-calendar notifications would not send. Please re-open the page and try again."
      );
      return;
    }

    // Keep recurrence identity + instance edits when editing an existing series
    const isRecurring = formData.repeat && formData.repeat !== "none";
    const keepSeriesId = isEditing && isRecurring && originalEvent?.recurrenceId;

        const normalizedParticipants = selectedMembers.map((m) => m.username).filter(Boolean);

    let actorName;
    try{actorName=await calendarActorName(db,userId,ctxUsername,activeTeamMembers);}catch{setErrorMessage('Your username could not be loaded. Please retry saving the event.');return;}
    const newEventObj = {
      ...(originalEvent || {}),
      eventId: draftEventId || (originalEvent?.eventId ?? uuidv4()),
      startUTC: toUtcISOString(startTime),
      endUTC: toUtcISOString(endTime),

      repeat: formData.repeat || "none",
      recurrenceId: isRecurring
        ? (keepSeriesId ? originalEvent.recurrenceId : uuidv4())
        : null,

      exceptions: keepSeriesId
        ? (Array.isArray(originalEvent.exceptions) ? originalEvent.exceptions : [])
        : [],

      overrides:
        keepSeriesId &&
        originalEvent.overrides &&
        typeof originalEvent.overrides === "object"
          ? originalEvent.overrides
          : {},

      repeatEndDate: isRecurring ? (formData.repeatEndDate || null) : null,

      tag,
      calendar,
      reminder,
      note: formData.customNote || "",
      mapId: mapId || null,
      mapName: mapName || null,
      ...calendarMatchDetails(formData),

      __teamId: undefined,
      createdAt: originalEvent?.createdAt || new Date().toISOString(),
      ...calendarCreatorFields(originalEvent,userId,actorName),

      ...(calendar === "team"
        ? {
            participants: normalizedParticipants,
            participantsUid,

            participantStates:
              originalEvent?.participantStates &&
              typeof originalEvent.participantStates === "object"
                ? originalEvent.participantStates
                : {},

            lastEditedByUid: userId,
            lastEditedBy: actorName,
            lastEditedAt: Date.now(),
          }
        : {}),
    };
  
// ✅ Optimistically mark queued participation requests as pending so badges render immediately
if (calendar === "team") {
  const queued = Object.values(queuedParticipationRequests || {});
  if (queued.length) {
    const nextStates = { ...(newEventObj.participantStates || {}) };
    queued.forEach((q) => {
      if (!q?.targetUid) return;
      nextStates[q.targetUid] = { status: "pending", at: Date.now() };
    });
    newEventObj.participantStates = nextStates;
  }
}


    try {
      if (!userId) throw new Error("User not logged in");
      const targetTeamId = (calendar === "team")
        ? editorTeamId
        : null;

      const docRef = calendar === "team"
        ? doc(db, "teams", targetTeamId)
        : doc(db, "users", userId);

      // ─── if this is a brand-new add (not editing), do a full sanitize & overwrite ───
      if (!isEditing) {
        // 1) fetch the existing array
        const snap = await getDoc(docRef);
        const currentEvents = snap.data().events || [];

        if (calendar === 'team' && eventPopup.fromAvailability) {
          const conflicts = findBookingConflicts(currentEvents, newEventObj,
            teamMembers.map(member => ({uid: member.uid, name: member.username})));
          const signature = JSON.stringify([newEventObj.startUTC, newEventObj.endUTC, participantsUid,
            conflicts.map(event => [event.eventId, event.startUTC, event.endUTC, event.participantsUid])]);
          if (conflicts.length && acceptedBookingConflict !== signature) {
            setBookingConflict({signature, message: `Selected participants are now booked for: ${conflicts.map(event => event.tag || 'Team event').join(', ')}. Change the time or confirm this conflict below.`});
            return;
          }
        }

        // 4) overwrite the whole array in Firestore
        await saveEventSetup({teamId: targetTeamId, scope: calendar, draft: sanitizeEvent(newEventObj)});

        // ✅ Only NOW (after successful save) send queued requests
        await flushQueuedParticipationRequests({ savedEventObj: newEventObj });

        // ✅ cleanup
        setQueuedParticipationRequests({});
        setLocalRequestStatusByUid({});
        setDraftEventId(null);

        setEventPopup(null);
        return;
      }


      // ─── otherwise we’re editing an existing series ─────────────────────────────
      const savedEvent = await saveEventSetup({teamId: targetTeamId, scope: calendar, draft: sanitizeEvent(newEventObj), previous: originalEvent, sourceTeamId: originalEvent.__teamId || teamId, sourceScope: originalEvent.calendar});


      // ✅ After successful save, flush queued requests
      await flushQueuedParticipationRequests({ savedEventObj: savedEvent });

      // ✅ cleanup
      setQueuedParticipationRequests({});
      setLocalRequestStatusByUid({});
      setDraftEventId(null);

      setEventPopup(null);
    } catch (err) {
      console.error("Failed to save/edit event:", err);
      setAlertMsg(err.message || "Failed to save/edit event.");
    }
  };
  
  

  const handleEditEvent = useCallback((event, clickX, clickY) => {
    if (readOnly) {
      setAlertMsg(readOnlyMessage);
      return;
    }

    const rawX = Number.isFinite(clickX) ? clickX : 100;
    const rawY = Number.isFinite(clickY) ? clickY : 100;

    setContextEvent(null);

    setDraftEventId(event?.eventId || uuidv4());
    setLocalRequestStatusByUid({});

    const localDate = new Date(
      new Date(event.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
    );

    const popupWidth = 280;
    const popupHeight = 360;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const clampedX = Math.min(rawX, viewportWidth - popupWidth - 10);
    const clampedY = Math.min(rawY, viewportHeight - popupHeight - 10);

    setEventPopup({
      x: clampedX,
      y: clampedY,
      date: localDate,
      isEditing: true,
      originalEvent: event,
    });

    setFormData({
      eventDate: eventDateInZone(event.startUTC, selectedTimeZone),
      repeatEndDate: event.repeatEndDate || "",
      startTime: localDate.toTimeString().slice(0, 5),
      endTime: new Date(
        new Date(event.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
      ).toTimeString().slice(0, 5),
      tag: event.tag,
      mapId: event.mapId || "",
      mapName: event.mapName || "",
      calendar: event.calendar,
      teamId: event.__teamId || teamId,
      reminder: event.reminder || "",
      customNote: event.note || "",
      ...calendarMatchDetails(event),
      repeat: event.repeat || "none",
      participants: Array.isArray(event.participants) ? event.participants : [],
    });

    setHasEditedEndTime(true);
  }, [readOnly, readOnlyMessage, selectedTimeZone, teamId]);

  useLayoutEffect(() => {
    if (!editorOnly || !editEvent || externalEditStarted.current) return;
    externalEditStarted.current = true;
    if (readOnly) {setAlertMsg(readOnlyMessage); return;}
    if (openActions) setContextEvent({event:editEvent,x:actionPosition?.x??window.innerWidth/2,y:actionPosition?.y??window.innerHeight/2,isRecurring:!!editEvent.repeat&&editEvent.repeat!=='none',isRecurringInstance:editEvent.isRecurringInstance});
    else if (editEvent.repeat && editEvent.repeat !== 'none') setRecurringActionModal({mode:'edit',event:editEvent});
    else handleEditEvent(editEvent,window.innerWidth/2,window.innerHeight/2);
  }, [editorOnly, openActions, actionPosition, editEvent, authReady, isUserLoading, teamLoading, loadedRosterTeamId, teamId, readOnly, readOnlyMessage, handleEditEvent]);

  useLayoutEffect(()=>{
    if(!editorOnly||!addDraft||externalEditStarted.current)return;
    externalEditStarted.current=true;
    if(readOnly){setAlertMsg(readOnlyMessage);return;}
    const date=DateTime.fromISO(addDraft.date,{zone:selectedTimeZone});
    if(!date.isValid){setAlertMsg('Choose a valid date for this event.');return;}
    const localDate=new Date(date.year,date.month-1,date.day,12);
    openAddPopup(localDate,window.innerWidth/2,window.innerHeight/2);
    setFormData(previous=>({...previous,calendar:addDraft.calendar||'personal',teamId:addDraft.teamId||teamId,
      ...(addDraft.startTime?{startTime:addDraft.startTime}:{}),...(addDraft.endTime?{endTime:addDraft.endTime}:{}),
      ...(Array.isArray(addDraft.uids)?{participants:teamMembers.filter(member=>addDraft.uids.includes(member.uid)).map(member=>member.username)}:{})}));
  },[editorOnly,addDraft,authReady,isUserLoading,teamLoading,loadedRosterTeamId,teamId,readOnly,selectedTimeZone,openAddPopup,readOnlyMessage,teamMembers]);

  const appliedDraftParticipants=useRef(false);
  useEffect(()=>{
    if(!editorOnly||!addDraft?.uids||appliedDraftParticipants.current||loadedRosterTeamId!==teamId)return;
    appliedDraftParticipants.current=true;
    const names=teamMembers.filter(member=>addDraft.uids.includes(member.uid)).map(member=>member.username);
    setFormData(previous=>({...previous,participants:[...new Set([...(previous.participants||[]),...names])]}));
  },[editorOnly,addDraft,loadedRosterTeamId,teamId,teamMembers]);

  useEffect(() => {
    if (!editorOnly) return;
    if (contextEvent || eventPopup || recurringActionModal || editingOverrideInstance || editingFutureSeries) externalEditorWasOpen.current = true;
    else if (externalEditorWasOpen.current && !externalMutationPending && !alertMsg) externalClose.current?.();
  }, [editorOnly, contextEvent, eventPopup, recurringActionModal, editingOverrideInstance, editingFutureSeries, externalMutationPending, alertMsg]);

  useEffect(() => {
    if (editorOnly) externalEditorRoot.current?.querySelector(recurringActionModal?'.recurring-modal button':'.event-popup button')?.focus();
  }, [editorOnly, contextEvent, eventPopup, recurringActionModal, editingOverrideInstance, editingFutureSeries]);
  
  
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('availabilityDraft');
    if (editorOnly || !id || consumedAvailabilityDraft.current === id || !authReady || isUserLoading || teamLoading || !userId || readOnly || !teamId || loadedRosterTeamId !== teamId) return;
    consumedAvailabilityDraft.current = id;
    try {
      const draft = JSON.parse(sessionStorage.getItem('availability-event-' + id) || 'null');
      if (!draft || draft.teamId !== teamId || Date.now() - draft.createdAt > 1800000 || !Array.isArray(draft.uids)) {
        throw new Error('This availability selection expired or belongs to another team. Please select the overlap again.');
      }
      const start = eventTimeToUTC(draft.date, draft.startTime, draft.timeZone);
      const end = eventTimeToUTC(draft.date, draft.endTime, draft.timeZone);
      if (!start || !end || end <= start) throw new Error('The selected overlap has an invalid date or time.');
      const participants = teamMembers.filter(member => draft.uids.includes(member.uid)).map(member => member.username);
      if (!participants.length) throw new Error('These participants are no longer on the team. Please select the overlap again.');
      const date = new Date(draft.date + 'T12:00:00');
      setSelectedTimeZone(draft.timeZone);
      setSelectedDate(date);
      setViewDate(date);
      const sources = calendarSourceDefaults('activeTeam', teamId);
      setSourcePreference({owner: userId, value: sources});
      try {sessionStorage.setItem('calendar.sources.v1.' + userId, JSON.stringify(sources));} catch {}
      setDraftEventId(uuidv4());
      setEventPopup({date, isEditing: false, fromAvailability: true, blockStartUTC: start, blockEndUTC: end, blockParticipantUids: teamMembers.filter(member => draft.uids.includes(member.uid)).map(member => member.uid)});
      setBookingConflict(null);
      setAcceptedBookingConflict(null);
      setFormData({eventDate: draft.date, startTime: draft.startTime, endTime: draft.endTime,
        calendar: 'team', participants, tag: '', reminder: '', repeat: 'none', repeatEndDate: '',
        customNote: '', mapId: '', mapName: ''});
      setHasEditedEndTime(true);
      setMissingFields([]);
      setErrorMessage('');
    } catch (error) {
      setAlertMsg(error.message || 'Could not open this availability selection.');
    }
    sessionStorage.removeItem('availability-event-' + id);
    params.delete('availabilityDraft');
    navigate({pathname: location.pathname, search: params.toString() ? '?' + params.toString() : ''}, {replace: true});
  }, [editorOnly, location.pathname, location.search, navigate, authReady, isUserLoading, teamLoading, userId, readOnly, teamId, teamMembers, loadedRosterTeamId]);

  // Availability event links select the actual occurrence date, including recurring events.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateKey = params.get('calendarDate');
    if (editorOnly || !dateKey || !userId || isUserLoading) return;
    const date = /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? new Date(dateKey + 'T12:00:00') : null;
    if (date && Number.isFinite(+date)) {
      setSelectedDate(date);
      setViewDate(date);
      const zone = params.get('calendarTimeZone');
      if (zone && DateTime.now().setZone(zone).isValid) setSelectedTimeZone(zone);
      const linkedTeam = params.get('calendarTeam');
      if (linkedTeam) {
        const sources = calendarSourceDefaults('activeTeam', linkedTeam);
        setSourcePreference({owner: userId, value: sources});
        try {sessionStorage.setItem('calendar.sources.v1.' + userId, JSON.stringify(sources));} catch {}
      }
    }
    ['calendarDate', 'calendarTeam', 'calendarTimeZone'].forEach(key => params.delete(key));
    navigate({pathname: location.pathname, search: params.toString() ? '?' + params.toString() : ''}, {replace: true});
  }, [editorOnly, location.pathname, location.search, navigate, userId, isUserLoading]);

  const deleteCalendarEvent = async (eventToDelete, future = false) => {
    if(editorValidationPending){setAlertMsg('Checking current event access. Please try again in a moment.');return;}
    if (readOnly) {showReadOnlyAlert(); return;}
    if (editorOnly) setExternalMutationPending(true);
    setContextEvent(null);
    const tid = eventToDelete.__teamId || teamId;
    try {
      await removeEventSetup({teamId: tid, scope: eventToDelete.calendar, event: eventToDelete, future,
        mutation: buildCalendarMutation({type: "cancelled", scope: future ? "future" : eventToDelete.instanceDate ? "instance" : "event",
          event: eventToDelete, actorUid: userId || null, actorName: ctxUsername || "Unknown"}),
      });
      if (!eventToDelete.instanceDate && eventToDelete.calendar === "team" && eventToDelete.eventId) {
        try {await deleteParticipationRequestsForEventFn({teamId: tid, eventId: eventToDelete.eventId});}
        catch (error) {console.warn("Failed to delete participation requests for event:", error);}
      }
    } catch (error) {console.error("Calendar deletion failed:", error); setAlertMsg("Could not delete this event. Please try again.");}
    finally {if (editorOnly) setExternalMutationPending(false);}
  };
  const handleDeleteEvent = event => deleteCalendarEvent(event);
  const handleDeleteFutureRecurringEvents = event => deleteCalendarEvent(event, true);

  useEffect(() => {
    if (teamId) setAlertMsg("");
  }, [teamId]);                         // runs only when teamId changes



  useEffect(() => {
    if (formData.calendar !== "team") return;
    if (!teamAvailIndex.ready) return;
    if (!eventPopup || !formData.startTime || !formData.endTime) return;

    const conflicts = (formData.participants || []).reduce((arr, name) => {
      const nameKey = String(name || "").toLowerCase();
      const m = teamMembers.find(x => String(x.username || "").toLowerCase() === nameKey);
      if (m && !isMemberAvailableForCurrentWindow(m.username, m.uid)) arr.push(name);
      return arr;
    }, []);
    setConflictSet(new Set(conflicts));

    if (conflicts.length) {
      const list = conflicts.slice(0,3).map(n => `'${n}'`).join(", ") +
        (conflicts.length > 3 ? ", …" : "");
      setAlertMsg(`Double check with ${list}, as event might conflict with their availabilities`);
    } else {
      setAlertMsg("");
    }
  }, [
    formData.calendar,
    formData.startTime,
    formData.endTime,
    formData.participants,
    eventPopup,
    selectedTimeZone,
    teamAvailIndex.ready,
    teamMembers,
    isMemberAvailableForCurrentWindow,
  ]);

  useEffect(() => {
    if (formData.calendar !== "team") {
      setQueuedParticipationRequests({});
      setLocalRequestStatusByUid({});
    }
  }, [formData.calendar]);


  return (
    <div ref={externalEditorRoot} className={`calendar-container calendar-workspace${editorOnly?' calendar-workspace--editor-only':''}`} onKeyDown={key=>{
      if (!editorOnly || key.defaultPrevented) return;
      if (key.key==='Escape') {key.preventDefault();externalClose.current?.();}
      if (key.key==='Tab') {
        const nodes=Array.from(externalEditorRoot.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href]'));
        const first=nodes[0],last=nodes[nodes.length-1];
        if (key.shiftKey&&document.activeElement===first) {key.preventDefault();last?.focus();}
        else if (!key.shiftKey&&document.activeElement===last) {key.preventDefault();first?.focus();}
      }
    }}>
      {editorOnly && !contextEvent && !eventPopup && !recurringActionModal && !editingOverrideInstance && !editingFutureSeries && <div className="overview-event-editor__loading"><p role="status">{alertMsg || 'Loading event editor…'}</p><button type="button" onClick={()=>externalClose.current?.()}>Close event editor</button></div>}
      {!editorOnly && <div className={`calendar-workspace__layout calendar-workspace__layout--agenda-${dayAgendaExpanded?'open':'closed'}`}><aside className="calendar-workspace__sidebar" aria-label="Calendar navigation">
        <header className="calendar-workspace__heading"><div><h1>Calendar</h1><p>Your schedule, across all your teams.</p></div></header>
        {isMobileUI && !readOnly && (
          <button
            className="cal-fab"
            type="button"
            aria-label="Add event"
            onClick={() => {
              const d = selectedDate || new Date();
              openAddPopupSmart(d, Math.round(window.innerWidth / 2), Math.round(window.innerHeight * 0.8));
            }}
          >
            +
          </button>
        )}
          <div className="month-view">
            <div className="calendar-nav">
              <div className="calendar-nav-left">
                <button type="button" className="month-arrow-class" aria-label="Previous month" onClick={goToPreviousMonth}><ChevronLeft size={16}/></button>
                <span>
                  {viewDate.toLocaleString("default", { month: "long" })} {viewDate.getFullYear()}
                </span>
                <button type="button" className="month-arrow-class" aria-label="Next month" onClick={goToNextMonth}><ChevronRight size={16}/></button>

              </div>

            </div>

            <div className="calendar-header">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div key={day} className="calendar-day-label">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-grid">
              {days.map((day, idx) => {
                const cellDate = new Date(currentYear, currentMonth, day);
                const isOutsideMonth = cellDate.getMonth() !== currentMonth;
                const isToday = cellDate.toDateString() === today.toDateString();
                const isSelected = cellDate.toDateString() === selectedDate.toDateString();

                  const eventsThisDay = expandedFilteredEvents.filter(e => {
                    const localDate = new Date(new Date(e.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone }));
                    return localDate.toDateString() === cellDate.toDateString();
                  });
                  
                return (
                  <div
                    key={idx}
                    role={day === null ? undefined : "button"}
                    tabIndex={day === null ? undefined : 0}
                    aria-label={day === null ? undefined : new Date(currentYear,currentMonth,day).toDateString()}
                    aria-pressed={day === null ? undefined : isSelected}
                    onKeyDown={e => {if (day !== null && (e.key === "Enter" || e.key === " ")) {e.preventDefault(); e.currentTarget.click();}}}
                    data-dateiso={`${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, "0")}-${String(cellDate.getDate()).padStart(2, "0")}`}
                    className={`calendar-cell ${isOutsideMonth ? "calendar-cell--outside-month" : ""} ${isToday ? "today" : ""} ${isSelected ? "selected" : ""} ${cellDate >= weekStart && cellDate <= weekEnd ? "in-selected-week" : ""}`}
                    onClick={(e) => {
                      if (day === null) return;

                      const clicked = new Date(currentYear, currentMonth, day);
                      const sameDay =
                        selectedDate.getFullYear() === clicked.getFullYear() &&
                        selectedDate.getMonth() === clicked.getMonth() &&
                        selectedDate.getDate() === clicked.getDate();

                      if (!sameDay) {
                        setSelectedDate(clicked);
                        return;
                      }

                      // Desktop: second click opens
                      if (isPointerFine) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const cx = rect.left + rect.width / 2;
                        const cy = rect.top + rect.height / 2;
                        openAddPopupSmart(clicked, cx, cy);
                      }
                    }}
                    onDoubleClick={(e) => {
                      if (day === null) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const cx = rect.left + rect.width / 2;
                      const cy = rect.top + rect.height / 2;
                      openAddPopupSmart(new Date(currentYear, currentMonth, day), cx, cy);
                    }}
                    onContextMenu={(e) => handleRightClick(e, day)}
                    // ✅ Mobile: long-press to add event
                    onTouchStart={(e) => {
                      if (day === null) return;
                      monthCellLongPress.start(e);
                    }}
                    onTouchMove={monthCellLongPress.move}
                    onTouchEnd={monthCellLongPress.end}
                  >
                    <div className="calendar-cell-content">
                      {cellDate.getDate()}
                      <div className="event-dots">
                        {eventsThisDay.slice(0, 5).map((event, i) => {
                          const tagColor = tagOptions.find(t => t.label === event.tag)?.color || "#999";
                          return (
                            <span
                              key={i}
                              className="event-dot"
                              style={{ backgroundColor: tagColor }}
                              title={`${eventTagLabel(event)} — ${getSourceLabel(event)}`}
                            />
                          );
                        })}
                          {eventsThisDay.length > 5 && (
                            <span className="event-dot event-dot-plus">+</span>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
      
          <CalendarSources names={teamNameById} selection={sourceSelection} onToggle={toggleCalendarSource} onSelectAll={selectAllCalendars} disabled={!authReady || !userId || isUserLoading}
            activeTeamId={teamId} teamAvailabilityDisabled={!authReady || isUserLoading || teamLoading}
            onTeamAvailability={() => navigate('/calendar/availability')} onMyAvailability={() => navigate('/calendar/my-availability')}/>
        </aside>
        <section className="calendar-agenda" data-view={display==='detailed'?calendarView:display} aria-label="Calendar schedule">
          <div className="calendar-agenda__toolbar"><div className="calendar-agenda__navigation">
            <button type="button" aria-label={display==='month'?'Previous calendar month':display==='list'||calendarView === 'week' ? 'Previous week' : 'Previous day'} onClick={() => moveSchedule(-1)}><ChevronLeft size={17}/></button>
            <button type="button" onClick={goToToday}>Today</button>
            <button type="button" aria-label={display==='month'?'Next calendar month':display==='list'||calendarView === 'week' ? 'Next week' : 'Next day'} onClick={() => moveSchedule(1)}><ChevronRight size={17}/></button>
          </div>
        {!readOnly && <button type="button" className="calendar-create-event" onClick={() => openAddPopupSmart(selectedDate || new Date(), Math.round(window.innerWidth / 2), 160)}><Plus size={18} aria-hidden="true"/>Create event</button>}
            <div className="timezone-wrapper"><Globe size={15} aria-hidden="true"/>
              <label>
                Time Zone:
                <select
                  value={selectedTimeZone}
                  onChange={(e) => setSelectedTimeZone(e.target.value)}
                  className="timezone-selector"
                >
                  {getTimeZoneOptions(selectedTimeZone).map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>


</div>
          <div className="calendar-display-switch" role="group" aria-label="Calendar display">
            {CALENDAR_DISPLAYS.map(([id,label])=><button type="button" key={id} aria-pressed={display===id} onClick={()=>changeDisplay(id)}>{label}</button>)}
          </div>
          <div className="calendar-agenda__scroll" ref={scheduleScrollRef}>
          {display!=='detailed'?<CalendarDisplayViews key={`${userId}:${display}`} display={display} date={display==='month'?viewDate:selectedDate} selectedDate={selectedDate} zone={selectedTimeZone} events={filteredEvents} teams={agendaTeams} names={teamNameById} uid={userId} username={ctxUsername} loading={isUserLoading} onSelectDate={selectAgendaDate} onOpenEvent={openContextMenuSmart} onAddEvent={readOnly?undefined:openAddPopupSmart}/>:calendarView === "day" ? (
            <>
              <div className="day-view-header-row">
                <h3 className="day-view-date-header">{selectedDate.toDateString()}</h3>

                {/* Day/Week toggle lives on the far right */}
                <div className="day-week-mode" role="tablist" aria-label="Calendar view">
                  <button
                    type="button"
                    className={`mode-btn ${calendarView === "day" ? "mode-active" : ""}`}
                    onClick={() => setViewAndPersist("day")}
                    role="tab"
                    aria-selected={calendarView === "day"}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    className={`mode-btn ${calendarView === "week" ? "mode-active" : ""}`}
                    onClick={() => setViewAndPersist("week")}
                    role="tab"
                    aria-selected={calendarView === "week"}
                  >
                    Week
                  </button>
                </div>
              </div>

              <div className="day-view expanded">
                <div className="day-timeline-wrapper expanded">
                  <div className="day-timeline-hours">
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="timeline-hour-label">{`${i}:00`}</div>
                    ))}
                  </div>

                  <div className="day-timeline" ref={dayTimelineRef}>
                    {
                      (() => {
                        // 🧹 Clear the refs before populating
                        eventRefs.current = [];

                        // 🗓️ Filter for just today’s events
                        const todaysEvents = expandedFilteredEvents
                          .filter(event => {
                            const localStart = new Date(new Date(event.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone }));
                            return (
                              localStart.getFullYear() === selectedDate.getFullYear() &&
                              localStart.getMonth() === selectedDate.getMonth() &&
                              localStart.getDate() === selectedDate.getDate()
                            );
                          })
                          .sort((a, b) => new Date(a.startUTC) - new Date(b.startUTC));
                        

                        return todaysEvents.map((event, idx, arr) => {
                          const localStart = new Date(new Date(event.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone }));
                          const localEnd = new Date(new Date(event.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone }));

                          const startMinutes = localStart.getHours() * 60 + localStart.getMinutes();
                          const endMinutes = localEnd.getHours() * 60 + localEnd.getMinutes();
                          const topPercent = (startMinutes / 1440) * 100;
                          const heightPercent = Math.max((endMinutes - startMinutes) / 1440 * 100, 2.5);

                          const overlappingEvents = arr.filter(e => {
                            const s = new Date(new Date(e.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })).getTime();
                            const eEnd = new Date(new Date(e.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })).getTime();
                            return !(eEnd <= localStart.getTime() || s >= localEnd.getTime());
                          });
                          const colIndex = overlappingEvents.findIndex(e => e === event);
                          const colCount = overlappingEvents.length;
                          const widthPercent = 100 / colCount;
                          const leftPercent = colIndex * widthPercent;

                          const tagColor = tagOptions.find(t => t.label === event.tag)?.color || "#ccc";
                          const maxTopPercent = 100 - heightPercent;
                          const adjustedTop = Math.min(topPercent, maxTopPercent);

                          return (
                            <div
                              key={idx}
                              className="timeline-event"
                              ref={(el) => {
                                if (el) eventRefs.current.push(el);
                              }}
                              style={{
                                top: `${adjustedTop}%`,
                                height: `${heightPercent}%`,
                                left: `${leftPercent}%`,
                                width: `${widthPercent}%`,
                                "--calendar-event-tag-color": tagColor,
                                overflow: "hidden",
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                openContextMenuSmart(event, e.clientX, e.clientY);
                              }}
                              onClick={(e) => {
                                if (!isMobileUI) return;
                                e.preventDefault();
                                openContextMenuSmart(event, e.clientX, e.clientY);
                              }}
                              onTouchStart={(e) => {
                                e.currentTarget.__evt = event;
                                eventLongPress.start(e);
                              }}
                              onTouchMove={eventLongPress.move}
                              onTouchEnd={(e) => {
                                eventLongPress.end(e);
                                e.currentTarget.__evt = null;
                              }}
                            >
                              <CalendarEventCard event={event} teamId={teamId} source={getSourceLabel(event)} start={localStart} end={localEnd}/>
                            </div>
                          );
                        });
                      })()
                    }
                  </div>
                </div>
              </div>
            </>      
          ) : (
            /* ====== WEEK VIEW (identical surface to Day) ====== */
            <>
              <div className="day-view-header-row">
                <h3 className="day-view-date-header">
                  {weekStart.toLocaleDateString(undefined, { month: "long", day: "numeric" })} –{" "}
                  {weekEnd.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                </h3>

                <div className="day-week-mode" role="tablist" aria-label="Calendar view">
                  <button
                    type="button"
                    className={`mode-btn ${calendarView === "day" ? "mode-active" : ""}`}
                    onClick={() => setViewAndPersist("day")}
                    role="tab"
                    aria-selected={calendarView === "day"}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    className={`mode-btn ${calendarView === "week" ? "mode-active" : ""}`}
                    onClick={() => setViewAndPersist("week")}
                    role="tab"
                    aria-selected={calendarView === "week"}
                  >
                    Week
                  </button>
                </div>
              </div>

              {/* Week day labels sit directly ABOVE the day-like rail */}
              <div className="week-strip-header">
                <div className="week-strip-hours-spacer" />
                {Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(weekStart);
                  d.setDate(weekStart.getDate() + i);
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} className={`week-strip-day ${isToday ? "today" : ""}`}>
                      {d.toLocaleDateString(undefined, { weekday: "short" })}{" "}
                      <span className="week-strip-num">{d.getDate()}</span>
                    </div>
                  );
                })}
              </div>

              {/* Same structure as Day view */}
              <div className="day-view expanded">
                <div className="day-timeline-wrapper expanded">
                  {/* reuse Day hours rail */}
                  <div className="day-timeline-hours">
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="timeline-hour-label">{`${i}:00`}</div>
                    ))}
                  </div>

                  {/* single shared timeline with 7 absolutely positioned "rails" */}
                  <div className="day-timeline week-rail" /* vertical guides painted by CSS */>
                    {Array.from({ length: 7 }, (_, dayIdx) => {
                      const colDate = new Date(weekStart);
                      colDate.setDate(weekStart.getDate() + dayIdx);

                      const dayEvents = expandedWeekEvents
                        .filter(evt => {
                          const local = new Date(
                            new Date(evt.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
                          );
                          return (
                            local.getFullYear() === colDate.getFullYear() &&
                            local.getMonth() === colDate.getMonth() &&
                            local.getDate() === colDate.getDate()
                          );
                        })
                        .sort((a, b) => new Date(a.startUTC) - new Date(b.startUTC));

                      const railLeftPct = (dayIdx * 100) / 7;
                      const railWidthPct = 100 / 7;

                      return (
                        <div
                          key={dayIdx}
                          className="week-day-rail"
                          style={{ left: `${railLeftPct}%`, width: `${railWidthPct}%` }}
                          onDoubleClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const cx = rect.left + rect.width / 2;
                            const cy = rect.top + rect.height / 2;
                            openAddPopup(colDate, cx, cy);
                          }}
                          title={colDate.toDateString()}
                        >
                          {dayEvents.map((event, idx, arr) => {
                            const localStart = new Date(
                              new Date(event.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
                            );
                            const localEnd = new Date(
                              new Date(event.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
                            );

                            const startMin = localStart.getHours() * 60 + localStart.getMinutes();
                            const endMin = localEnd.getHours() * 60 + localEnd.getMinutes();
                            const topPct = (startMin / 1440) * 100;
                            const heightPct = Math.max(((endMin - startMin) / 1440) * 100, 2.5);

                            // overlap sizing *within this day rail*
                            const overlapping = arr.filter(e => {
                              const s = new Date(
                                new Date(e.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
                              ).getTime();
                              const ee = new Date(
                                new Date(e.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })
                              ).getTime();
                              return !(ee <= localStart.getTime() || s >= localEnd.getTime());
                            });
                            const colIndex = overlapping.findIndex(e => e === event);
                            const colCount = overlapping.length;
                            const widthPct = 100 / colCount;
                            const leftPct = colIndex * widthPct;

                            const tagColor = tagOptions.find(t => t.label === event.tag)?.color || "#CCCCCC";
                            const maxTopPct = 100 - heightPct;
                            const adjustedTop = Math.min(topPct, maxTopPct);

                            return (
                              <div
                                key={idx}
                                className="timeline-event"
                                style={{
                                  top: `${adjustedTop}%`,
                                  height: `${heightPct}%`,
                                  left: `${leftPct}%`,
                                  width: `${widthPct}%`,
                                  "--calendar-event-tag-color": tagColor
                                }}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  openContextMenuSmart(event, e.clientX, e.clientY);
                                }}
                                onClick={(e) => {
                                  if (!isMobileUI) return;
                                  e.preventDefault();
                                  openContextMenuSmart(event, e.clientX, e.clientY);
                                }}
                                onTouchStart={(e) => {
                                  e.currentTarget.__evt = event;
                                  eventLongPress.start(e);
                                }}
                                onTouchMove={eventLongPress.move}
                                onTouchEnd={(e) => {
                                  eventLongPress.end(e);
                                  e.currentTarget.__evt = null;
                                }}

                                title={`${eventTagLabel(event)}${event.note ? ` — ${event.note}` : ""}`}
                              >
                                <CalendarEventCard event={event} teamId={teamId} source={getSourceLabel(event)} start={localStart} end={localEnd}/>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>

          )}
          </div>
          <div className="calendar-agenda__footer" role="status">{selectedSourceNames.length ? 'Showing ' + selectedSourceNames.join(', ') : 'No calendars selected. Check a calendar to show its events.'}</div>
        </section>
        <CalendarDayAgenda date={selectedDate} timeZone={selectedTimeZone} events={expandedAgendaEvents} expanded={dayAgendaExpanded} onExpandedChange={changeDayAgendaExpanded} onSelectDate={selectAgendaDate} onOpenEvent={openContextMenuSmart} onEditEvent={readOnly?undefined:(event,x,y)=>{if(event.repeat && event.repeat!=="none"){setContextEvent(null);setRecurringActionModal({mode:"edit",event});}else{handleEditEvent(event,x,y);}}} teamId={teamId} teamNames={teamNameById} teams={agendaTeams} members={activeTeamMembers} loading={isUserLoading} hasSelectedCalendars={selectedSourceNames.length>0}/>
      </div>
      }
      {contextEvent && <EventActionMenu x={contextEvent.x} y={contextEvent.y} recurring={contextEvent.isRecurring} readOnly={readOnly} message={readOnlyMessage} onClose={()=>setContextEvent(null)} onAction={action=>{
        const event=contextEvent.event;
        if(action==='edit')handleEditEvent(event,contextEvent.x,contextEvent.y);
        else if(action==='delete'||action==='delete-instance')handleDeleteEvent(event);
        else if(action==='delete-future')handleDeleteFutureRecurringEvents(event);
        else {
          setContextEvent(null);
          if(action==='edit-instance')setEditingOverrideInstance(event);else setEditingFutureSeries(event);
          setFormData({eventDate:eventDateInZone(event.startUTC,selectedTimeZone),startTime:DateTime.fromISO(event.startUTC,{zone:selectedTimeZone}).toFormat('HH:mm'),endTime:DateTime.fromISO(event.endUTC,{zone:selectedTimeZone}).toFormat('HH:mm'),tag:event.tag||'',customNote:event.note||'',...calendarMatchDetails(event),repeat:event.repeat||'none',repeatEndDate:event.repeatEndDate||'',calendar:event.calendar,teamId:event.__teamId||teamId,reminder:event.reminder||'',mapId:event.mapId||'',mapName:event.mapName||'',participants:Array.isArray(event.participants)?event.participants:[]});
        }
      }}/>}

      {(eventPopup?.fromAvailability || eventPopup?.splitBlock) && <EventBlockScheduler teamId={editorTeamId} startUTC={eventPopup.blockStartUTC} endUTC={eventPopup.blockEndUTC} participants={eventPopup.blockParticipantUids} initialEvent={eventPopup.blockInitialEvent} members={teamMembers} maps={teamMaps} onClose={closePopup}
        timeZone={selectedTimeZone} availabilities={teamAvailRaw} onRequestParticipation={async (event, uids) => {
          const results = await Promise.allSettled(uids.map(targetUid => requestEventParticipationFn({teamId: editorTeamId,
            eventId: event.eventId, instanceDate: event.instanceDate || null, eventTag: event.tag, eventStartUTC: event.startUTC, eventEndUTC: event.endUTC,
            timeZone: selectedTimeZone, note: event.note || '', participantUsernames: event.participants || [], targetUid})));
          if (results.some(result => result.status === 'rejected')) setAlertMsg('Events saved, but some participation requests could not be sent.');
        }}
        onBack={eventPopup.splitBlock ? () => setEventPopup(previous => ({...previous, splitBlock: false})) : undefined}/> }
      {eventPopup && !eventPopup.fromAvailability && !eventPopup.splitBlock && (() => {

        return (
          <>
            <div className="event-popup-backdrop" onClick={closePopup} />
            <div
              className="event-popup event-popup--editor"
              ref={popupRef}
            >
              <div className="event-popup__content">
                <button className="popup-close-btn app-cancel-control" aria-label="Close event menu" onClick={closePopup}>×</button>
                
                <h4>{eventPopup?.isEditing ? "Edit Event" : "Add Event"}</h4>
                <EventDateControl value={formData.eventDate || `${eventPopup.date.getFullYear()}-${String(eventPopup.date.getMonth()+1).padStart(2,'0')}-${String(eventPopup.date.getDate()).padStart(2,'0')}`}
                  onChange={eventDate => setFormData(previous => ({...previous, eventDate}))}/>

                <label>
                  Calendar:
                  <div className="calendar-toggle calendar-destination">
                    <button
                      type="button"
                      className={formData.calendar === "personal" ? "toggle-active" : ""}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, calendar: "personal" }))
                      }
                    >
                      Personal Calendar
                    </button>
                    <button
                      type="button"
                      className={formData.calendar === "team" ? "toggle-active" : ""}
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          calendar: "team",
                          teamId: prev.teamId || teamId,
                          participants: []
                        }))
                      }
                    >
                      Team Calendar
                    </button>
                    {formData.calendar === "team" && <select className="calendar-team-selector" aria-label="Team calendar" value={editorTeamId || ''} onChange={e => {
                      setFormData(prev => ({...prev, teamId: e.target.value, participants: [], mapId: '', mapName: '', mapSlots: []}));
                      setQueuedParticipationRequests({}); setLocalRequestStatusByUid({}); setErrorMessage('');
                    }}>
                      {!editorTeamId && <option value="">Select a team</option>}
                      {Object.entries(teamNameById).sort((a,b) => a[1].localeCompare(b[1])).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                    </select>}
                  </div>
                </label>

                <div className={`event-editor__body ${formData.calendar === "team" ? "event-editor__body--team" : ""}`}>
                  <section className="event-editor__details">
                    <h5>Event Details</h5>
                    <div className="event-editor__fields">
                <label>
                  Start Time:
                  <select
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleFormChange}
                    style={{
                      border: missingFields.includes("startTime")
                        ? "2px solid #FF4D4F"
                        : undefined,
                    }}
                  >
                    {generateTimeOptions().map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  End Time:
                  <select
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleFormChange}
                    style={{
                      border: missingFields.includes("endTime")
                        ? "2px solid #FF4D4F"
                        : undefined,
                    }}
                  >
                    {generateTimeOptions().map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                    <option value="24:00">24:00 (midnight)</option>
                  </select>
                </label>
                
                  {!eventPopup.isEditing && !readOnly && <button className="event-editor__divide" type="button" onClick={() => {
                    const startUTC = toUtcISOStringAt(formData.eventDate ?? eventPopup.date, formData.startTime);
                    const endUTC = toUtcISOStringAt(formData.eventDate ?? eventPopup.date, formData.endTime);
                    if (!startUTC || !endUTC || Date.parse(endUTC) <= Date.parse(startUTC)) {setErrorMessage("Choose the full time block's start and end time first."); return;}
                    if (formData.calendar === "team" && (!editorTeamId || loadedRosterTeamId !== editorTeamId)) {setErrorMessage("Wait for the team roster to finish loading, then try again."); return;}
                    const people = teamMembers.filter(member => (formData.participants || []).includes(member.username) && member.uid);
                    setEventPopup(previous => ({...previous, splitBlock: true, blockStartUTC: startUTC, blockEndUTC: endUTC,
                      blockParticipantUids: people.map(member => member.uid), blockInitialEvent: {
                        ...calendarMatchDetails(formData), calendar: formData.calendar, tag: formData.tag,
                        note: formData.customNote || "", reminder: formData.reminder || "", repeat: formData.repeat || "none",
                        repeatEndDate: formData.repeatEndDate || "", mapId: formData.mapId || "", mapName: formData.mapName || "",
                      }}));
                  }}>Divide into multiple events</button>}
                <label>
                  Repeat:
                  <select name="repeat" value={formData.repeat} onChange={handleFormChange}>
                    <option value="none">None</option>
                    <option value="daily">Every Day</option>
                    <option value="weekly">Every Week</option>
                    <option value="biweekly">Every 2 Weeks</option>
                  </select>
                </label>
                {formData.repeat !== "none" && (
                  <label>
                    Repeats Until:
                    <input
                      type="date"
                      name="repeatEndDate"
                      value={formData.repeatEndDate}
                      onChange={handleFormChange}
                    />
                  </label>
                )}



                <label>
                  Event Tag:
                  <select
                    name="tag"
                    value={formData.tag}
                    onChange={handleFormChange}
                    style={{
                      color:
                        tagOptions.find((t) => t.label === formData.tag)?.color || "#999",
                      fontWeight: "bold",
                      border: missingFields.includes("tag")
                        ? "2px solid #FF4D4F"
                        : undefined,
                    }}
                  >
                    <option value="">Please Select</option>
                    {tagOptions.map((tag) => (
                      <option
                        key={tag.label}
                        value={tag.label}
                        style={{ color: tag.color }}
                      >
                        {tag.label}
                      </option>
                    ))}
                  </select>
                </label>

                <MatchDetailsFields value={formData} onChange={setFormData} maps={teamMaps}/>


                <label className="event-editor__full">
                  Reminder:
                  <select
                    name="reminder"
                    value={formData.reminder}
                    onChange={handleFormChange}
                  >
                    <option value="">None</option>
                    <option value="30min">30 minutes before</option>
                    <option value="2h">2 hours before</option>
                    <option value="24h">24 hours before</option>
                    <option value="48h">48 hours before</option>
                    <option value="7d">7 days before</option>
                  </select>
                </label>
                <label className="event-editor__full">
                  Custom Note:
                  <textarea
                    name="customNote"
                    value={formData.customNote || ""}
                    onChange={handleFormChange}
                    maxLength={50}
                    placeholder="Optional note (max 50 chars)"
                    rows={2}
                  />
                  <span className="event-editor__note-count">{(formData.customNote || "").length}/50</span>
                </label>


                    </div>
                  </section>
                {formData.calendar === "team" && (
                  <div className="form-group event-editor__participants">
                    <div className="participants-header">
                      <span className="form-label">Participants</span>
                      {conflictSet.size > 0 && (
                        <span className="conflict-count-badge" title="Not available for the selected time">
                          <TriangleAlert size={12} aria-hidden="true"/> {conflictSet.size} conflict{conflictSet.size>1 ? "s" : ""}
                        </span>
                      )}
                      {/* <div className="participants-toolbar">
                        <button type="button" onClick={() => {
                          const all = teamMembers.map(m => m.username);
                          setFormData(prev => ({ ...prev, participants: all }));
                        }}>Select All</button>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, participants: [] }))}>Clear</button>
                        <button type="button"
                          className={showConflictsOnly ? "active" : ""}
                          onClick={() => setShowConflictsOnly(v => !v)}>
                          {showConflictsOnly ? "Show All" : "Conflicts Only"}
                        </button>
                      </div> */}
                    </div>
                    <div className="participants-wrap">
                      <div className="participants-toolbar">
                        <button
                          type="button"
                          onClick={() =>
                            {
                              const all = teamMembers.map(m => m.username);
                              setFormData(prev => ({ ...prev, participants: all }));
                              // Non-blocking combined warning
                              if (
                                formData.calendar === "team" &&
                                teamAvailIndex.ready &&
                                eventPopup &&
                                formData.startTime && formData.endTime
                              ) {
                                const conflicts = teamMembers
                                  .filter(m => !isMemberAvailableForCurrentWindow(m.username, m.uid))
                                  .map(m => m.username);
                                if (conflicts.length) {
                                  const list = conflicts.slice(0, 3).map(n => `'${n}'`).join(", ") + (conflicts.length > 3 ? ", …" : "");
                                  setAlertMsg(`${list} may have availability conflicts. Confirmation requests will be sent automatically after this event is saved.`);
                                }
                              }
                            }
                          }
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData(prev => ({ ...prev, participants: [] }))
                          }
                        >
                          Clear
                        </button>
                      </div>

                      <div className="participants-list">
                        {teamMembers.length === 0 && (
                          <div className="participants-empty">No team members found.</div>
                        )}
                        {teamMembers
                          .filter(m => !showConflictsOnly || conflictSet.has(m.username))
                          .map(member => {
                          const name = member.username;
                          const checked = formData.participants.includes(name);
                          const hasConflict = conflictSet.has(name);
                          const isQueued = !!queuedParticipationRequests?.[member.uid];
                          return (
                            <label
                              key={name}
                              className={`participant-row ${hasConflict ? "has-conflict" : ""}`}
                            >
                              <input
                                type="checkbox"
                                className="participant-checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setFormData(prev => {
                                    const set = new Set(prev.participants);
                                    if (e.target.checked) set.add(name);
                                    else set.delete(name);
                                    return { ...prev, participants: Array.from(set) };
                                  });
                                  if (e.target.checked) warnIfUnavailable(member);
                                }}
                              />

                              <div className="participant-info">
                                <div className="participant-topline">
                                  <span
                                    className="participant-username"
                                    style={{ color: roleColor(member.role), fontWeight: 700 }}
                                    title={member.role ? `${name} — ${normalizeRole(member.role)}` : name}
                                  >
                                    {name}
                                  </span>
                                  <span className="event-editor__role" style={{ color: roleColor(member.role) }}>{normalizeRole(member.role)}</span>
                                </div>

                                {hasConflict && member?.uid && member.uid !== userId && (() => {
                                  const status = getEffectiveRequestStatus(member.uid);
                                  const isPending = status === "pending";
                                  const isAccepted = status === "accepted";
                                  const isDeclined = status === "declined";

                                  return (
                                    <div className="CalendarView-conflictBlock">
                                      <div className="CalendarView-conflictActionRow">
                                        <span className="CalendarView-conflictActionText" title="Potential conflict">
                                          <TriangleAlert size={12} aria-hidden="true"/> potential conflict
                                        </span>

                                        <button
                                          type="button"
                                          className={`CalendarView-requestIconBtn ${isQueued ? "is-queued" : ""}`}
                                          disabled={isQueued || isPending || isAccepted || isDeclined}
                                          onClick={() => queueParticipationRequest(member)}
                                          aria-label={
                                            isQueued
                                              ? `Queued request for ${member?.username || "member"}`
                                              : isPending
                                                ? `Request already pending for ${member?.username || "member"}`
                                                : isAccepted
                                                  ? `${member?.username || "Member"} already accepted`
                                                  : isDeclined
                                                    ? `${member?.username || "Member"} already declined`
                                                    : `Queue participation confirmation request for ${member?.username || "member"}`
                                          }
                                          title={
                                            isQueued
                                              ? "Participation request will be sent upon the addition of this event."
                                              : isPending
                                                ? "Request already sent (pending)"
                                                : isAccepted
                                                  ? "Already accepted"
                                                  : isDeclined
                                                    ? "Already declined"
                                                    : "Queue participation confirmation request"
                                          }
                                        >
                                          {isQueued ? (
                                            <svg viewBox="0 0 24 24" aria-hidden="true" className="CalendarView-requestIcon">
                                              <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                                            </svg>
                                          ) : (
                                            <svg viewBox="0 0 24 24" aria-hidden="true" className="CalendarView-requestIcon">
                                              <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm6-6V11a6 6 0 1 0-12 0v5L4 18v1h16v-1l-2-2z" />
                                            </svg>
                                          )}
                                        </button>
                                      </div>

                                      {isQueued && (
                                        <div
                                          className="CalendarView-conflictQueuedNote"
                                          title="Participation request will be sent upon the addition of this event."
                                        >
                                          Queued
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              

                </div>
                {errorMessage && (
                  <p className="calendar-error-message">{errorMessage}</p>
                )}
                {eventPopup.fromAvailability && bookingConflict && <div className="calendar-error-message">
                  <p>{bookingConflict.message}</p>
                  <label><input type="checkbox" checked={acceptedBookingConflict === bookingConflict.signature}
                    onChange={e => setAcceptedBookingConflict(e.target.checked ? bookingConflict.signature : null)} />
                    I reviewed these conflicts and want to schedule this event anyway.
                  </label>
                </div>}

                <div className="popup-buttons event-editor__footer">
                  <button type="button" className="event-editor__cancel app-cancel-control" onClick={closePopup}>Cancel</button>

                  {readOnly ? (
                    <p className="calendar-error-message">{readOnlyMessage}</p>
                  ) : (
                    <button onClick={handleSaveEvent}>
                      {eventPopup?.isEditing ? "Save Changes" : "Add Event"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      })()}
      {recurringActionModal && (
        <div className="recurring-modal-backdrop">
          <div className="recurring-modal">
            <h4>This is a recurring event</h4>
            <p>What would you like to do?</p>

            <div className="popup-buttons">
              {readOnly ? (
                <p className="calendar-error-message">{readOnlyMessage}</p>
              ) : recurringActionModal.mode === "edit" && (
                <>
                  <button onClick={() => {
                    setRecurringActionModal(null);
                    setEditingOverrideInstance(recurringActionModal.event);
                    setFormData({
                      eventDate: eventDateInZone(recurringActionModal.event.startUTC, selectedTimeZone),
                      startTime: new Date(new Date(recurringActionModal.event.startUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })).toTimeString().slice(0, 5),
                      endTime: new Date(new Date(recurringActionModal.event.endUTC).toLocaleString("en-US", { timeZone: selectedTimeZone })).toTimeString().slice(0, 5),
                      customNote: recurringActionModal.event.note || "",
                      ...calendarMatchDetails(recurringActionModal.event),
                      tag: recurringActionModal.event.tag || "",
                      mapId: recurringActionModal.event.mapId || "",
                      mapName: recurringActionModal.event.mapName || "",
                    });
                  }}>
                    Edit This Instance
                  </button>

                  <button onClick={() => {
                    // 1) close the modal
                    setRecurringActionModal(null);
                    // 2) stash the baseEvent into editingFutureSeries
                    const base = recurringActionModal.event;
                    setEditingFutureSeries(base);
                    // 3) prefill your formData with the current occurrence’s values:
                      setFormData({
                      eventDate: eventDateInZone(base.startUTC, selectedTimeZone),
                      startTime: new Date(
                        new Date(base.startUTC)
                          .toLocaleString("en-US", { timeZone: selectedTimeZone })
                      ).toTimeString().slice(0,5),
                      endTime: new Date(
                        new Date(base.endUTC)
                          .toLocaleString("en-US", { timeZone: selectedTimeZone })
                      ).toTimeString().slice(0,5),
                      tag: base.tag,
                      customNote: base.note || "",
                      ...calendarMatchDetails(base),
                      repeat: base.repeat || "none",
                      repeatEndDate: base.repeatEndDate || "",
                      calendar: base.calendar,
                      reminder: base.reminder || "",
                      mapId: base.mapId || "",
                      mapName: base.mapName || "",
                    });
                  }}>
                    Edit This and All Future
                  </button>
                </>
              )}


              {!readOnly && recurringActionModal.mode === "delete" && (
                <>
                  <button onClick={() => {
                    setRecurringActionModal(null);
                    handleDeleteEvent(recurringActionModal.event); // just this
                  }}>Delete This Instance</button>

                  <button onClick={() => {
                    setRecurringActionModal(null);
                    handleDeleteFutureRecurringEvents(recurringActionModal.event);
                  }}>Delete This and All Future</button>



                </>
              )}

              <button className={'app-cancel-control'} onClick={() => setRecurringActionModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {editingOverrideInstance && (() => {
        const pos = clampPopupPosition(120, 120);

        return (
          <div
            className="event-popup"
            ref={popupRef}
            style={{ ...pos, "--popup-top": `${pos.top}px` }}
          >
            <div className="event-popup__content">
              <button className="popup-close-btn app-cancel-control" onClick={() => setEditingOverrideInstance(null)}>×</button>

              <h4>Edit Single Instance</h4>
              <MatchDetailsFields value={formData} onChange={setFormData} maps={teamMaps}/>
              <label>
                Event Date:
                <input type="date" name="eventDate" required
                  value={formData.eventDate || ""} onChange={handleFormChange} />
              </label>

              <label>
                Start Time:
                <select
                  value={formData.startTime}
                  onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                >
                  {generateTimeOptions().map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>

              <label>
                End Time:
                <select
                  value={formData.endTime}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                >
                  {generateTimeOptions().map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>

              <label>
                Event Tag:
                <select
                  value={formData.tag}
                  name="tag"
                  onChange={handleFormChange}
                  style={{
                    color: tagOptions.find((t) => t.label === formData.tag)?.color || "#999",
                    fontWeight: "bold"
                  }}
                >
                  {tagOptions.map((tag) => (
                    <option
                      key={tag.label}
                      value={tag.label}
                      style={{ color: tag.color }}
                    >
                      {tag.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Note:
                <input
                  type="text"
                  maxLength={50}
                  value={formData.customNote || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, customNote: e.target.value }))}
                />
              </label>


              <div className="popup-buttons">
                <button onClick={async () => {
                  if(editorValidationPending){setErrorMessage('Checking current event access. Please try again in a moment.');return;}
                  if (readOnly) {
                    showReadOnlyAlert();
                    return;
                  }

                  const baseEvent = editingOverrideInstance;

                  const startUTC = toUtcISOStringAt(formData.eventDate, formData.startTime);
                  const endUTC = toUtcISOStringAt(formData.eventDate, formData.endTime);
                  if (!startUTC || !endUTC || endUTC <= startUTC) {
                    setAlertMsg("Please choose a valid date and an end time after the start time.");
                    return;
                  }
                  const override = {
                    startUTC,
                    endUTC,
                    note: formData.customNote || "",
                    tag: formData.tag || baseEvent.tag,
                    ...calendarMatchDetails(formData),
                  };              

                  const tid = baseEvent.__teamId || teamId;
                  try {
                    await saveEventSetup({teamId: tid, scope: baseEvent.calendar, previous: baseEvent, draft: {...baseEvent, ...override}});
                  } catch (failure) {setAlertMsg(failure.message || "Could not save this occurrence."); return;}
                  setEditingOverrideInstance(null);
                }}>
                  Save
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {editingFutureSeries && (() => {
        const pos = clampPopupPosition(120, 120);

        return (
          <div
            className="event-popup"
            ref={popupRef}
            style={{ ...pos, "--popup-top": `${pos.top}px` }}
          >
            <div className="event-popup__content">
              <button className="popup-close-btn app-cancel-control" onClick={() => setEditingFutureSeries(null)}>×</button>
              <h4>Edit This & All Future Occurrences</h4>
              <MatchDetailsFields value={formData} onChange={setFormData} maps={teamMaps}/>
              <label>
                Event Date:
                <input type="date" name="eventDate" required
                  value={formData.eventDate || ""} onChange={handleFormChange} />
              </label>

              <label>
                Start Time:
                <select value={formData.startTime}
                        onChange={handleFormChange}
                        name="startTime">
                  {generateTimeOptions().map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label>
                End Time:
                <select value={formData.endTime}
                        onChange={handleFormChange}
                        name="endTime">
                  {generateTimeOptions().map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label>
                Event Tag:
                <select value={formData.tag}
                        onChange={handleFormChange}
                        name="tag">
                  {tagOptions.map(tag => (
                    <option key={tag.label} value={tag.label}>{tag.label}</option>
                  ))}
                </select>
              </label>

              <label>
                Note:
                <input type="text"
                      name="customNote"
                      maxLength={50}
                      value={formData.customNote}
                      onChange={handleFormChange}/>
              </label>

              <div className="popup-buttons">
                <button onClick={async () => {
                  if(editorValidationPending){setErrorMessage('Checking current event access. Please try again in a moment.');return;}
                  if (readOnly) {
                    showReadOnlyAlert();
                    return;
                  }

                  const base = editingFutureSeries;
                  const tid = base.__teamId || teamId;
                  // 3) build new series event (UTC must be derived from selectedTimeZone, NOT system zone)
                  const startUTC = toUtcISOStringAt(formData.eventDate, formData.startTime);
                  const endUTC   = toUtcISOStringAt(formData.eventDate, formData.endTime);

                  if (!startUTC || !endUTC || endUTC <= startUTC) {
                    setAlertMsg("Failed to convert times to UTC. Please try again.");
                    return;
                  }

                  let actorName;
                  try{actorName=await calendarActorName(db,userId,ctxUsername,activeTeamMembers);}catch{setAlertMsg('Your username could not be loaded. Please retry saving the event.');return;}
                  const newEvt = {
                    eventId: uuidv4(),
                    startUTC,
                    endUTC,
                    repeat: base.repeat,
                    recurrenceId: uuidv4(),
                    exceptions: [],
                    overrides: {},
                    repeatEndDate: formData.repeatEndDate || null,
                    tag: formData.tag,
                    mapId: base.mapId || null,
                    mapName: base.mapName || null,
                    calendar: base.calendar,
                    reminder: base.reminder || "",
                    note: formData.customNote,
                    ...calendarMatchDetails(formData),
                    createdAt: new Date().toISOString(),
                    ...calendarCreatorFields(base,userId,actorName),

                    participants: Array.isArray(base.participants) ? base.participants : [],
                    participantsUid: Array.isArray(base.participantsUid) ? base.participantsUid : [],
                    participantStates:
                      base.participantStates && typeof base.participantStates === "object"
                        ? base.participantStates
                        : {},

                    lastEditedByUid: userId,
                    lastEditedBy: actorName,
                    lastEditedAt: Date.now(),
                  };

                  try {
                    await splitEventSetupSeries({teamId: tid, scope: base.calendar, previous: base, draft: newEvt});
                  } catch (error) {setAlertMsg(error.message || "Could not update this series."); return;}
                  setEditingFutureSeries(null);
                }}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {alertMsg && (
        <CustomAlert
          message={alertMsg}
          type="warning"
          position="top"
          onClose={() => setAlertMsg("")}
          actions={[{ label: "Review", onClick: () => {
            setShowConflictsOnly(true);
            // keep the alert open UX-simple: just close it
            setAlertMsg("");
          }}]}
        />
      )}
      {authActionAlert && (
        <CustomAlert
          message={authActionAlert}
          type="auth"
          position="top"
          autoCloseMs={3200}
          onClose={() => setAuthActionAlert("")}
          actions={[
            {
              label: "Login",
              onClick: () => {
                setAuthActionAlert("");
                navigate("/login");
              },
            },
          ]}
        />
      )}

    </div>
  );
};

export default CalendarView;
